/**
 * Die gezeichnete Tagesszene über Zuhause (2026-08-29).
 *
 * WARUM ES SIE GIBT
 *
 *   Martin: *„Bitte mach Zuhause viel schöner und einladender, so dass man da
 *   gerne hingeht, das ist so langweilig und ausdruckslos! Vielleicht mit
 *   Bildern oder Symbolen, das muss viel lebendiger werden!"*
 *
 *   Ein Bild, das jeden Tag gleich aussieht, ist Tapete. Diese Szene folgt
 *   dem WETTER und der TAGESZEIT — morgens ein anderer Himmel als abends,
 *   bei Regen Regenzüge statt Sonnenstrahlen. Sie sagt also etwas, statt zu
 *   schmücken. Das ist der Unterschied zwischen lebendig und dekoriert.
 *
 * WARUM KEIN FOTO
 *
 *   Ein Foto brächte fremde Farben ins warme Papier und müsste geladen
 *   werden. Striche nehmen die Textfarbe an und tragen jede Palette mit —
 *   dieselbe Begründung wie bei `zeichen.ts`, nur eine Größe darüber.
 *
 * ⚠️ KEINE EMOJI, auch nicht als Abkürzung. Siehe zeichen.ts.
 */

import { jahreszeitAmDatum, wienerMonat, type Jahreszeit } from "../../shared/jahreszeit.js";

export type { Jahreszeit } from "../../shared/jahreszeit.js";
export type Wetterlage = "regen" | "sonne" | "wolken" | "unbekannt";
export type Tageszeit = "morgen" | "mittag" | "abend" | "nacht";

/** Wiener Stunde — die Szene richtet sich nach Martins Tag, nicht nach UTC. */
/** Sonnenauf- und -untergang in oertlicher Zeit ("2026-08-29T19:48"). */
export interface Sonnenzeiten {
  aufgang: string;
  untergang: string;
}

/** Minuten seit Mitternacht aus einer oertlichen ISO-Zeit ohne Zonenangabe. */
function minuten(iso: string): number | null {
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/**
 * Welche Tageszeit ist es?
 *
 * ⚠️ 2026-08-29 — Martin: *„Die Sonne ist immer noch in dem Bild vorhanden,
 * obwohl es schon acht Uhr am Abend ist. Du musst natuerlich auch schauen,
 * wie die Zeitzonen verlaufen, denn jetzt ist dieses bei uns schon dunkel."*
 *
 * Hier standen FESTE Stunden: Abend bis 21 Uhr, dann Nacht. Ende August geht
 * die Sonne bei ihm gegen 19:50 unter — um 20 Uhr stand sie im Bild, waehrend
 * draussen Dunkelheit war.
 *
 * Feste Stunden koennen das nicht wissen. Der Untergang haengt an Datum UND
 * Ort: in Hollenthon liegt er im Juni zweieinhalb Stunden spaeter als im
 * Dezember, und wer verreist, verschiebt ihn nochmal. Eine Zahl im Code ist
 * dafuer das falsche Werkzeug.
 *
 * Deshalb entscheiden jetzt die ECHTEN Zeiten, sofern sie da sind — sie kommen
 * mit dem Wetter, das den Ort ohnehin kennt. Die festen Stunden bleiben als
 * Rueckfall, wenn kein Wetter vorliegt; sie sind grob, aber besser als nichts.
 */
/**
 * Minuten seit Mitternacht in WIEN — Martins Tag, nicht die Uhr des Geräts.
 *
 * ⚠️ `hourCycle: "h23"`: mit `hour12: false` liefern manche Laufzeiten um
 *    Mitternacht „24" statt „00". Für das Licht im Haus (aus ab 0 Uhr) wäre
 *    genau diese Minute falsch.
 */
export function wienerMinute(d: Date): number {
  const teile = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Vienna",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const wert = (typ: string): number => Number(teile.find((t) => t.type === typ)?.value ?? 0);
  return (wert("hour") % 24) * 60 + wert("minute");
}

/**
 * Wie lange vor dem Sonnenaufgang schon Morgenröte ist, in Minuten.
 * (10.10.2026)
 *
 * Martin, 06:59 Uhr, mit Bild: *„Die Anzeige von dem Bild im Zuhause ist noch
 * im Nachtmodus, obwohl ich, wenn ich beim Fenster rausschaue, schon eine
 * leichte Morgenröte sehe."* Der Aufgang war erst um 07:03; bis dahin galt
 * alles als Nacht (Sterne, Mond, tiefes Blau).
 *
 * Die bürgerliche Dämmerung dauert in Hollenthon 33 bis 40 Minuten; 30 sind
 * die vorsichtige Zahl, ab der man draußen sicher einen hellen Streifen sieht.
 * ⚠️ NUR am Morgen. Abends bleibt es bei der Entscheidung vom 29.08. (die
 * Dämmerung zählt zur Nacht — Martins Einwand war die Sonne bei Dunkelheit).
 */
export const MORGENROETE_MIN = 30;

/** Wie tief der SCHEIN zu Beginn der Morgenröte unter dem fernen Hügel steht (Punkte). */
const SONNE_TIEF = 34;
/** Wie tief die SCHEIBE ganz unten steht: Radius 23 plus Rand — ihre Oberkante bleibt unter der Hügelkante. */
const SCHEIBE_TIEF = 34;
/** In den letzten so vielen Minuten vor dem Aufgang steigt die Scheibe bis an die Kante (dort steht sie zur Aufgangszeit, wie immer). */
const SONNE_AUFSTIEG_MIN = 4;

/** Minuten bis zum Aufgang (> 0 = noch nicht aufgegangen), oder null ohne Zeiten. */
function minutenBisAufgang(d: Date, sonne?: Partial<Sonnenzeiten> | null): number | null {
  const auf = sonne?.aufgang ? minuten(sonne.aufgang) : null;
  if (auf === null) return null;
  return auf - wienerMinute(d);
}

/**
 * Ist gerade Morgenröte: die Szene zeigt den Morgenhimmel, aber die Sonne ist
 * noch nicht über den Hügel? Nur mit echten Zeiten — ohne sie gäbe es keinen
 * Aufgang, auf den man warten könnte.
 */
export function morgenroete(
  d: Date,
  zeit: Tageszeit,
  sonne?: Partial<Sonnenzeiten> | null,
): boolean {
  if (zeit !== "morgen") return false;
  const bis = minutenBisAufgang(d, sonne);
  return bis !== null && bis > 0 && bis <= MORGENROETE_MIN;
}

export function tageszeitJetzt(
  d: Date = new Date(),
  sonne?: Sonnenzeiten | null,
): Tageszeit {
  const min = wienerMinute(d);
  const h = Math.floor(min / 60);
  const auf = sonne ? minuten(sonne.aufgang) : null;
  const unter = sonne ? minuten(sonne.untergang) : null;
  if (auf !== null && unter !== null && unter > auf) {
    /*
     * ⚠️ Die Daemmerung zaehlt zur Nacht, nicht zum Abend. Eine halbe Stunde
     * nach Sonnenuntergang steht keine Sonne mehr am Himmel — genau darum
     * ging Martins Einwand. Vor dem Aufgang galt dasselbe, bis zum 10.10.2026:
     * die letzten MORGENROETE_MIN Minuten vor dem Aufgang sind jetzt Morgen.
     */
    if (min < auf - MORGENROETE_MIN || min >= unter) return "nacht";
    // Die erste Stunde nach dem Aufgang und die letzte vor dem Untergang
    // sind die tiefstehenden — dazwischen liegt der hohe Tag.
    if (min < auf + 150) return "morgen";
    if (min >= unter - 120) return "abend";
    return "mittag";
  }
  // Rueckfall ohne Wetter: grobe Stunden.
  if (h < 5 || h >= 21) return "nacht";
  if (h < 11) return "morgen";
  if (h < 17) return "mittag";
  return "abend";
}

/**
 * Aus dem Wortlaut der Vorhersage die Lage ableiten.
 *
 * ⚠️ Bewusst grob: Es geht um drei Bilder, nicht um Meteorologie. Was nicht
 * eindeutig ist, wird „unbekannt" — dann zeichnet die Szene den ruhigen
 * Grundzustand statt einer falschen Behauptung.
 */
export function wetterlageAus(beschreibung?: string | null): Wetterlage {
  const s = (beschreibung ?? "").toLowerCase();
  if (!s) return "unbekannt";
  if (/regen|schauer|niesel|graupel|schnee|gewitter/.test(s)) return "regen";
  if (/klar|sonnig|heiter|sonne/.test(s)) return "sonne";
  if (/bewölkt|bedeckt|wolk|nebel|trüb|dunst/.test(s)) return "wolken";
  return "unbekannt";
}

/** Der Himmel hinter der Szene — zwei Töne, aus der Palette. */
/*
 * ⚠️ Hier stand `himmel(zeit)` und lieferte ein Farbpaar als INLINE-STIL.
 *
 * Martin: *„Sollte sich das nicht entsprechend dem Morgen, dem Mittag, dem
 * Abend anpassen? Und vielleicht kann man es noch schöner machen."*
 *
 * Es passte sich an — nur fast unsichtbar. Ausgelesen im Browser:
 *
 *   morgen   paper-soft → paper
 *   mittag   paper-elev → paper
 *   abend    paper      → paper-soft
 *   nacht    paper-soft → paper      ← WORTGLEICH mit morgen
 *
 * Vier Tageszeiten, drei fast gleiche Grüns, und die Nacht heller als der
 * Abend. Dazu stand die Sonne immer auf demselben Fleck.
 *
 * Der Himmel liegt jetzt im Blatt, angesteuert über `data-zeit`. Zwei
 * Gründe: Inline-Stile gehören nicht ins Markup, und nur im Blatt lässt
 * sich der dunkle Modus getrennt behandeln — dort ist `--ink` HELL, eine
 * Mischung damit würde die Nacht aufhellen statt sie zu vertiefen.
 */

/** Sonne bei Tag, Mond bei Nacht — an derselben Stelle. */
/**
 * Wo die Sonne steht — und wie hoch.
 *
 * ⚠️ Das ist die Änderung, die man wirklich SIEHT. Vorher stand sie immer
 * auf `cx=300, cy=52`; morgens wie abends dasselbe Bild.
 *
 * Der Bogen läuft von links unten über die Mitte nach rechts unten. Er
 * bleibt in der rechten Hälfte des Himmels, weil links die Wolke steht
 * (x 43–135) — eine Sonne dahinter sähe aus wie ein Fehler, nicht wie
 * Wetter. Tief heißt zusätzlich GRÖSSER: eine tiefstehende Sonne wirkt
 * näher, und die Hügel schneiden sie an, weil sie nach ihr gezeichnet
 * werden.
 */
/* ═══════════════════════════════════════════════════════════════════
   DIE BAHN — 2026-09-04

   Martin: *„Wär's nicht schöner, wenn die Sonne wirklich im Osten aufgeht
   und im Westen runter, also sich über den Tag über das gesamte Bild dreht?
   Zum Mittag steht sie in der Mitte. Das Gleiche für den Mond."*

   Ja. Vorher gab es DREI feste Punkte (morgen/mittag/abend), alle in der
   rechten Bildhälfte — die Sonne sprang zwischen ihnen, statt zu wandern.

   Jetzt ein Bogen über die ganze Breite: links auf, in der Mitte hoch,
   rechts unter. Beide Enden liegen auf der Höhe des fernen Hügels, also
   geht sie dort HINTER ihm auf und unter (siehe z-index der Ebenen).

   ⚠️ Der Fortschritt kommt aus den ECHTEN Zeiten, wenn das Wetter sie
   liefert (`sonne.aufgang`/`untergang`, örtliche Zeit). Ohne sie gelten
   feste Stunden — dann stimmt der Bogen ungefähr statt genau, aber er
   springt nicht.
   ═══════════════════════════════════════════════════════════════════ */

/** Oberkante des fernen Hügels — dort gehen die Gestirne auf und unter. */
const HORIZONT = 104;
/** Höchster Punkt der Bahn (Mittag bzw. Mitternacht). */
const GIPFEL = 32;
const BAHN_LINKS = 46;
const BAHN_RECHTS = 344;

/**
 * Die Spur des Tages. (06.09.2026)
 *
 * Martin: *„Ich möchte gerne wissen, ob die Sonne und der Mond sich überhaupt
 * bewegen. Während ich draufschau, sehe ich, dass er sich nicht verändert."*
 *
 * Sie bewegt sich, und zwar richtig — aber gemessen sind es **0,37 Einheiten
 * je Minute**, bei einer Bahn von 298 Einheiten Breite. In einer Minute ist
 * das ein Drittel Punkt, in zehn Minuten knapp vier. Wer hinsieht, sieht
 * nichts; wer eine Stunde spaeter wiederkommt, sieht 22 Punkte Unterschied.
 * Genau so verhaelt sich die echte Sonne auch.
 *
 * Schneller machen waere gelogen. Was hilft, ist die BAHN zu zeigen: Steht
 * die Sonne auf einer sichtbaren Spur, sieht man auf einen Blick, wo im Tag
 * sie ist — knapp ueber dem Horizont oder schon im Abstieg. Die Bewegung
 * wird dadurch nicht schneller, aber sie wird lesbar.
 *
 * ⚠️ Sehr zart. Eine kraeftige Linie waere ein Diagramm, kein Himmel.
 */
function bahnSpur(): string {
  const a = bahnPunkt(0);
  const m = bahnPunkt(0.5);
  const e = bahnPunkt(1);
  // Eine quadratische Kurve durch die drei Punkte trifft den Sinus nah genug.
  const ctrlY = 2 * m.y - (a.y + e.y) / 2;
  return `<path class="szene-bahnspur" d="M${a.x} ${a.y}Q${m.x} ${ctrlY} ${e.x} ${e.y}"
    fill="none" stroke="var(--ink)" stroke-width="0.8" stroke-linecap="round"
    stroke-dasharray="1 7" opacity="0.10"/>`;
}

/** Punkt auf der Bahn. `f` = 0 (Aufgang) bis 1 (Untergang). */
export function bahnPunkt(f: number): { x: number; y: number } {
  const t = Math.min(1, Math.max(0, f));
  return {
    x: Math.round(BAHN_LINKS + (BAHN_RECHTS - BAHN_LINKS) * t),
    y: Math.round(HORIZONT - Math.sin(Math.PI * t) * (HORIZONT - GIPFEL)),
  };
}

/**
 * Wie weit ist der Lauf? Tags von Auf- bis Untergang, nachts von Unter- bis
 * zum nächsten Aufgang.
 *
 * ⚠️ Ohne echte Zeiten gelten 6:30 und 20:00 — ungefähr statt genau. Ein
 * ungefährer Bogen ist immer noch besser als drei Sprünge.
 */
export function bahnFortschritt(
  jetzt: Date,
  sonne?: Partial<Sonnenzeiten>,
  nacht = false,
): number {
  // ⚠️ `minuten()` gibt es hier schon — keine zweite Fassung daneben.
  const auf = (sonne?.aufgang ? minuten(sonne.aufgang) : null) ?? 6 * 60 + 30;
  const unter =
    (sonne?.untergang ? minuten(sonne.untergang) : null) ?? 20 * 60;
  const nun = jetzt.getHours() * 60 + jetzt.getMinutes();
  if (!nacht) {
    const spanne = Math.max(1, unter - auf);
    return (nun - auf) / spanne;
  }
  // Nacht: vom Untergang bis zum Aufgang am Folgetag.
  const spanne = Math.max(1, 24 * 60 - unter + auf);
  const seit = nun >= unter ? nun - unter : 24 * 60 - unter + nun;
  return seit / spanne;
}

/**
 * Wo die Sonne jetzt steht — auch VOR dem Aufgang. (10.10.2026)
 *
 * Zwei Höhen, weil Schein und Scheibe nicht zugleich aufgehen:
 *   y         der SCHEIN (weiter Hof): steigt in den letzten MORGENROETE_MIN
 *             Minuten vor dem Aufgang von tief unter dem fernen Hügel bis an
 *             dessen Kante und färbt den Horizont von unten — die Morgenröte.
 *   scheibeY  die SCHEIBE samt Korona, Kern und Strahlen: bleibt bis kurz vor
 *             dem Aufgang ganz unter dem Hügel (Probe 10.10.: 06:59 bei Aufgang
 *             07:03 — eine halbe Sonne wäre gelogen) und steigt in den letzten
 *             SONNE_AUFSTIEG_MIN Minuten bis an die Kante, wo sie zur Aufgangszeit
 *             steht wie bisher (Halbmond-Lage, siehe gestirn-hinter-huegel).
 * Ab dem Aufgang gilt die Bahn ({@link bahnPunkt}), Schein und Scheibe liegen
 * dann auf derselben Höhe. Ein Stand, kein Sprung: dieselben Schichten wandern
 * jede Minute weiter.
 */
export function sonnenPunkt(
  jetzt: Date,
  sonne?: Partial<Sonnenzeiten>,
): { x: number; y: number; scheibeY: number } {
  const auf = (sonne?.aufgang ? minuten(sonne.aufgang) : null) ?? 6 * 60 + 30;
  const bis = auf - wienerMinute(jetzt);
  if (bis > 0) {
    const tiefe = Math.min(1, bis / MORGENROETE_MIN);
    const aufstieg = Math.min(1, bis / SONNE_AUFSTIEG_MIN);
    return {
      x: BAHN_LINKS,
      y: Math.round(HORIZONT + SONNE_TIEF * tiefe),
      scheibeY: Math.round(HORIZONT + SCHEIBE_TIEF * aufstieg),
    };
  }
  const b = bahnPunkt(bahnFortschritt(jetzt, sonne, false));
  return { x: b.x, y: b.y, scheibeY: b.y };
}

/* ═══════════════════════════════════════════════════════════════════
   DER STERN ATMET — 15.09.2026 (Ticket 2297)

   Martin: *„Kann man auch ein dynamisches Element einbauen, so wie wir es
   auf der Webseite haben, also in der App, mit dem Mond und der Sonne? Das
   waere eine super Erweiterung, irgendwo also wieder ein Erkennungswert zur
   Vespera-App."*

   Die Webseite, die er als Vorbild nennt, hat weder Sonne noch Mond — sie
   hat den wandernden Grund. Der Wert, den er beschreibt, ist: es weiss,
   welche Stunde ist. In der App traegt genau ein Zeichen diese Rolle: der
   Stern im Kopf. Die Wortmarke ist ueberall ausgeblendet
   (`.topbar .brand-text { display: none }`), er ist das einzige
   Identitaetszeichen, und er steht auf jeder Seite.

   ⚠️ NUR ueber die vorhandenen Tokens. `--ember` ist NICHT ueberall warm:
      Wald hell #2f6b45 (gruen), Ozean hell #2f7cae (blau), Sand #9a6c20.
      Ein fest eingebautes Morgengold waere in zwei von vier Paletten ein
      Fremdkoerper. Deshalb wandert hier nur das MISCHVERHAELTNIS zwischen
      --ember und --sage, der WINKEL des Verlaufs und die Tiefe des Hofs.
      Keine neuen Farbwerte.

   ⚠️ Die Richtung ist der Natur abgeschaut: tiefstehendes Licht ist warm,
      hochstehendes neutral, die Nacht kuehl. Morgen und Abend teilen
      deshalb dieselbe Waerme — unterscheidbar bleiben sie am Winkel, der
      mit dem Lauf von links nach rechts wandert.
   ═══════════════════════════════════════════════════════════════════ */

interface Sternstand {
  /** Wie viel Glut im Verlauf steckt: 0 ganz kuehl, 1 ganz warm. */
  glut: number;
  /** Drehung des Verlaufs in Grad, -38 (Osten) bis +38 (Westen). */
  winkel: number;
  /** Staerke des Hofs: 0 am Tag, bis 1 in der tiefen Nacht. */
  hof: number;
}

/** Auf zwei Stellen — sonst schreibt jede Minute einen neuen Stilwert. */
function zweiStellen(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Wie der Stern gerade steht.
 *
 * `fortschritt` ist der Lauf aus {@link bahnFortschritt} — tags von Auf- bis
 * Untergang, nachts von Unter- bis zum naechsten Aufgang. Der Bogen ist an
 * den Raendern 0 und in der Mitte 1; genau daran haengt die Waerme.
 */
export function sternstand(nacht: boolean, fortschritt: number): Sternstand {
  const f = Math.min(1, Math.max(0, fortschritt));
  const bogen = Math.sin(Math.PI * f);
  // Der Verlauf wandert mit dem Lauf von Osten nach Westen — das ist es,
  // was Morgen und Abend trotz gleicher Waerme unterscheidbar macht.
  const winkel = zweiStellen(-38 + 76 * f);
  if (nacht) {
    return {
      glut: zweiStellen(0.42 - 0.22 * bogen),
      winkel,
      hof: zweiStellen(bogen),
    };
  }
  return { glut: zweiStellen(0.94 - 0.48 * bogen), winkel, hof: 0 };
}

/**
 * Der Glut-Anteil der beiden Haltepunkte, als Prozentzahl fuer color-mix.
 *
 * Oben traegt der Stern die Glut, unten die Ruhe — das ist der Verlauf, den
 * es seit jeher gibt. Die Stunde verschiebt nur, wie weit die beiden
 * auseinanderliegen.
 */
export function sternHaltepunkte(glut: number): { oben: number; unten: number } {
  const g = Math.min(1, Math.max(0, glut));
  return { oben: Math.round(35 + 60 * g), unten: Math.round(8 + 22 * g) };
}

/* ═══════════════════════════════════════════════════════════════════
   DIE MONDPHASE — 2026-09-04

   Martin: *„Das Gleiche für den Mond. Dass auch die Sichel in der richtigen
   Position ist."*

   Der Mond war eine feste Sichel, immer gleich geöffnet. Jetzt wird die
   echte Phase gerechnet und gezeichnet: Neumond, zunehmende Sichel, Halb,
   zunehmend voll, Vollmond — und zurück.

   ⚠️ Zunehmend ist rechts hell, abnehmend links (Nordhalbkugel). Eine
   Sichel, die falsch herum steht, fällt jedem auf, der je hochgeschaut hat.

   Die Rechnung ist die übliche Näherung: Zeit seit einem bekannten Neumond,
   geteilt durch den synodischen Monat. Auf ein paar Stunden genau — für ein
   gezeichnetes Bild mehr als genug.
   ═══════════════════════════════════════════════════════════════════ */

const SYNODISCHER_MONAT_MS = 29.530588853 * 86_400_000;
/** Bekannter Neumond: 6. Januar 2000, 18:14 UTC. */
const NEUMOND_BEZUG_MS = Date.UTC(2000, 0, 6, 18, 14);

/**
 * Wie der Mond heute heißt — und ob er wächst oder schwindet. (05.09.2026)
 *
 * Martin: *„Der Mond muss entweder abnehmen oder zunehmen. Das ist auch noch
 * wichtig, damit ich das sehe, und das kann man übrigens auch dazuschreiben:
 * bei Zuhause, ob wir ab- oder zunehmenden Mond haben."*
 *
 * Gezeichnet wurde die Phase schon richtig (rechts hell = zunehmend). Sie
 * war nur nicht zu LESEN — und tagsüber steht am Himmel gar kein Mond, also
 * half das Bild allein nicht.
 *
 * ⚠️ „Zunehmend" ist nicht dasselbe wie „nimmt zu": Der Vollmond wächst
 *    nicht mehr, der Neumond schwindet nicht. Beide bekommen deshalb kein
 *    Richtungswort.
 */
export function mondName(phase: number): string {
  const p = ((phase % 1) + 1) % 1;
  if (p < 0.03 || p > 0.97) return "Neumond";
  if (Math.abs(p - 0.5) < 0.03) return "Vollmond";
  if (Math.abs(p - 0.25) < 0.04) return "zunehmender Halbmond";
  if (Math.abs(p - 0.75) < 0.04) return "abnehmender Halbmond";
  if (p < 0.25) return "zunehmende Sichel";
  if (p < 0.5) return "zunehmender Mond";
  if (p < 0.75) return "abnehmender Mond";
  return "abnehmende Sichel";
}

/** 0 = Neumond, 0.25 = zunehmend halb, 0.5 = Vollmond, 0.75 = abnehmend halb. */
export function mondphase(jetzt: Date): number {
  const seit = jetzt.getTime() - NEUMOND_BEZUG_MS;
  const rest = ((seit % SYNODISCHER_MONAT_MS) + SYNODISCHER_MONAT_MS) %
    SYNODISCHER_MONAT_MS;
  return rest / SYNODISCHER_MONAT_MS;
}

/**
 * Der beleuchtete Teil als Pfad.
 *
 * Aufbau: der Rand ist immer ein Halbkreis, die Trennlinie eine Ellipse,
 * deren Breite mit der Phase geht. Bei Halbmond ist sie eine Gerade, bei
 * Vollmond ein voller Kreis in der Gegenrichtung.
 *
 * ⚠️ Nicht als zwei Kreise mit Deckfarbe gebaut: die Szene liegt auf einem
 * VERLAUF, ein deckender Kreis haette eine sichtbare Naht gesetzt — dieselbe
 * Falle wie bei der Wolke am 03.09.
 *
 * ⚠️ 05.09.2026 — Martin: „Der Mond soll genau die Größe haben, die er
 *    wirklich hat, damit das Bild rund und richtig ist." Zweimal sah es so
 *    aus, als rage die helle Fläche bei den fast vollen Phasen über den Rand.
 *    Pixel gezählt (Playwright, r=160): bei ZEHN Phasen null Pixel klar
 *    außerhalb, weitester Punkt r=160,7 — Rasterrand. Die Geometrie hier
 *    war richtig; alle vier anderen Bogenrichtungen liefern falsche
 *    Helligkeiten. Der Eindruck kam aus der ZEICHNUNG in `gestirn()`: Die
 *    helle Fläche trug eine dickere, hellere Kontur als der Mondrand und
 *    wurde NACH ihm gemalt. Jetzt liegt der Rand oben, gleich stark. Wer
 *    hier an der Formel dreht, macht die Helligkeit kaputt — messen, nicht
 *    schauen.
 */
export function mondPfad(cx: number, cy: number, r: number, phase: number): string {
  const zunehmend = phase < 0.5;
  // k geht von 1 (Neumond) ueber 0 (Halb) bis -1 (Vollmond).
  const k = Math.cos(2 * Math.PI * phase);
  const rx = Math.abs(k) * r;
  // Aussenbogen: rechte Haelfte bei zunehmend, linke bei abnehmend.
  const aussen = zunehmend ? 1 : 0;
  /*
   * ⚠️ Die Richtung des Rueckbogens. Im ersten Anlauf stand hier
   * `k > 0 ? aussen : 1 - aussen` — genau verkehrt, und im Bild sofort zu
   * sehen: Neumond zeigte eine VOLLE Scheibe, Vollmond eine leere.
   *
   * Der Grund: Laeuft der Rueckbogen mit derselben Drehrichtung wie der
   * Aussenbogen, geht er um die ANDERE Seite herum — dann ist die Flaeche
   * der ganze Kreis statt null. Er muss dieselbe Seite zurueck.
   */
  const innen = k > 0 ? 1 - aussen : aussen;
  return (
    `M${cx} ${cy - r}` +
    `A${r} ${r} 0 0 ${aussen} ${cx} ${cy + r}` +
    `A${rx.toFixed(1)} ${r} 0 0 ${innen} ${cx} ${cy - r}Z`
  );
}

/**
 * Ein paar Sterne — nur nachts, nur wenige.
 *
 * ⚠️ Bewusst ohne Zufall: dieselbe Seite soll bei jedem Aufbau gleich
 * aussehen. Springende Sterne bei jedem Neuzeichnen wären Unruhe, kein
 * Nachthimmel.
 */
/**
 * Der Kasten, den die Wolke am Himmel belegt — mit etwas Polster.
 *
 * ⚠️ Steht HIER und nicht in `wolke()`, weil ihn zwei brauchen: die Wolke
 * zeichnet darin, die Sterne halten sich daraus heraus. Zwei Zahlensaetze
 * fuer dieselbe Form waeren zwei Gelegenheiten, sie auseinanderlaufen zu
 * lassen — und man saehe es erst nachts bei Bewoelkung.
 */
const WOLKE_KASTEN = { x1: 54, y1: 12, x2: 140, y2: 70 };

/**
 * Sterne — dieselben acht bei jedem Aufbau.
 *
 * @param verdeckt Steht eine Wolke am Himmel? Dann fallen die Sterne weg,
 *   die hinter ihr laegen.
 *
 * ⚠️ 2026-09-03 (Martin: *„weil die Wolke, da schimmern die Sterne
 * durch"*): Die Wolke ist eine KONTUR — das SVG steht auf `fill="none"`,
 * ihr Inneres ist also leer, und was dahinter liegt, ist zu sehen. Sie zu
 * fuellen waere der naheliegende Weg, aber der Himmel ist ein VERLAUF; eine
 * einfarbige Fuellung haette eine sichtbare Naht ergeben. Also nicht die
 * Wolke undurchsichtig machen, sondern die zwei Sterne weglassen, die
 * dahinter stehen. Das haelt den Strich-Stil rein und kann keine Naht
 * erzeugen.
 */
/**
 * Sternschnuppen — jede Stunde woanders. (06.09.2026)
 *
 * Martin: *„Die Sternschnuppe sieht total toll aus. Vielleicht kann man sie
 * auch immer an unterschiedlichen Stellen machen, weil Sternschnuppen ja
 * auch immer wieder anders sind."*
 *
 * Vorher standen Anfang und Ende der Bahn fest im Blatt (300/8 → 226/64) —
 * dieselbe Stelle, jede Nacht. Jetzt kommt der Startpunkt aus der Stunde:
 * Die Szene wird stuendlich neu gebaut, also faellt jede Stunde eine an
 * einer anderen Stelle.
 *
 * ⚠️ Gerechnet, nicht gewuerfelt. `Math.random()` beim Zeichnen hiesse, dass
 *    die Schnuppe bei jedem Neuaufbau springt — und die Szene wird auch aus
 *    anderen Gruenden neu gebaut. Aus der Stunde gerechnet bleibt sie
 *    innerhalb einer Stunde, wo sie ist.
 *
 * ⚠️ Der Schweif zeigt ENTGEGEN der Flugrichtung (Martin, 05.09.: „es faellt
 *    einfach nur ein Strich"). Die Bahn geht nach links-unten, der Schweif
 *    also nach rechts-oben — beides bleibt fest, nur die Stelle wandert.
 */
function sternschnuppen(jetzt: Date): string {
  const strich =
    '<circle cx="0" cy="0" r="1.4" fill="var(--szene-mond)"/>' +
    '<line x1="0" y1="0" x2="10" y2="-7.6" stroke="var(--szene-mond)" stroke-width="1.3" stroke-linecap="round"/>' +
    '<line x1="0" y1="0" x2="24" y2="-18.2" stroke="var(--szene-mond)" stroke-width="0.8" stroke-opacity="0.45" stroke-linecap="round"/>';
  // Stunde seit einem festen Punkt — eine Zahl, die sich stuendlich aendert.
  const stunde = Math.floor(jetzt.getTime() / 3_600_000);
  const stelle = (n: number): string => {
    // Zwei teilerfremde Streuer, damit x und y nicht im Gleichschritt wandern.
    const x = 110 + (((stunde * 37 + n * 101) % 260) | 0);
    const y = 4 + (((stunde * 23 + n * 61) % 42) | 0);
    return `--schnuppe-x:${x}px;--schnuppe-y:${y}px`;
  };
  return `<g class="szene-sternschnuppe" style="${stelle(0)}">${strich}</g>
      <g class="szene-sternschnuppe szene-sternschnuppe--zwei" style="${stelle(1)}">${strich}</g>`;
}

/**
 * Eine kleine Überraschung — manchmal. (05.09.2026, Martin: „dass Du dir
 * eine kleine Überraschung überlegst, die hier zufällig kommt, für den Tag
 * und für die Nacht.")
 *
 * `wuerfel` liegt zwischen 0 und 1 und kommt vom Aufrufer (im Betrieb
 * Math.random je Ladung, im Test eine feste Zahl). Über 0,45: nichts — das
 * ist der häufigste Fall, sonst wäre es keine Überraschung mehr.
 */
const SELTEN_NACHT = `<g class="szene-laterne">
        <path d="M0 0c-4 0-6 3-6 8c0 5 2 9 6 9s6-4 6-9c0-5-2-8-6-8z" fill="var(--szene-mond)" fill-opacity="0.55"/>
        <path d="M-2 17h4" stroke="var(--szene-mond)" stroke-width="1"/>
        <circle cx="0" cy="8" r="2.2" fill="var(--szene-mond)" fill-opacity="0.9"/>
      </g>`;
const SELTEN_TAG = `<g class="szene-drachen" stroke="var(--ink)" stroke-width="1.2" stroke-linejoin="round" opacity="0.6">
        <path d="M0-12l8 10-8 14-8-14z" fill="var(--szene-sonne)" fill-opacity="0.4"/>
        <path d="M0-12v26M-8-2h16"/>
        <path d="M0 12c-2 5-6 6-8 10c-1 3 1 5 3 4" fill="none"/>
        <path d="M-4 20l-2-1.5 2-1.5zM-7 26l-2-1.5 2-1.5z" fill="var(--ink)" fill-opacity="0.35"/>
        <path d="M-1 4c-14 24-30 42-52 70" fill="none" stroke-width="0.7" stroke-opacity="0.6"/>
      </g>`;

export interface Ueberraschung {
  /** Im Himmel-Bild — hinter den Hügeln. */
  himmel: string;
  /** Im Boden-Bild — vor den Hügeln, über der Wiese. */
  boden: string;
}
const NICHTS: Ueberraschung = { himmel: "", boden: "" };
export function ueberraschung(
  zeit: Tageszeit,
  wuerfel: number,
  lage: Wetterlage = "unbekannt",
): Ueberraschung {
  if (!(wuerfel < 0.45)) return NICHTS;
  /*
   * ⚠️ 01.10.2026 (Ticket 2892) — das Wetter bleibt massgeblich. Bei Regen
   * fliegt weder Ballon noch Drachen, und ein Schmetterling sitzt in keiner
   * Wolkendecke: eine Ueberraschung, die dem Wetter widerspricht, ist ein
   * Fehler im Bild, keine Ueberraschung. Der Schmetterling gehoert dem
   * sonnigen Tag; unter Wolken bleibt es beim Ballon.
   */
  if (lage === "regen") return NICHTS;
  // Martin: „Ich wollte auch überrascht werden. Jetzt weiß ich ja, was
  // kommt." — Diese hier steht nirgends beschrieben. Selten, mit Absicht.
  if (wuerfel < 0.03) return zeit === "nacht" ? { himmel: SELTEN_NACHT, boden: "" } : { himmel: SELTEN_TAG, boden: "" };
  const erste = wuerfel < 0.225;
  if (zeit === "nacht") {
    // Die Glühwürmchen sind fester Nachtbestand (siehe gluehwuermchen) —
    // die Nacht braucht eine Bewegung, die immer da ist, so wie der Tag die
    // Vögel hat. Als Überraschung bleibt der Satellit.
    return erste
      ? { himmel: `<g class="szene-satellit"><circle cx="0" cy="0" r="1.3" fill="var(--szene-mond)" opacity="0.7"/></g>`, boden: "" }
      : NICHTS;
  }
  // 06.09.: nach dem ersten Bild neu gezeichnet — der Ballon las sich als
  // Glühbirne, der Schmetterling als Knäuel und flatterte durch die Sonne.
  // Jetzt: Hülle mit Bahnen, Seile, Korb; der Schmetterling mit zwei Flügeln
  // VOR den Hügeln, über der Wiese — dort, wo er hingehört.
  const schmetterlingOk = lage === "sonne" || lage === "unbekannt";
  if (!erste && !schmetterlingOk) return NICHTS;
  return erste
    ? { himmel: `<g class="szene-ballon" stroke="var(--ink)" stroke-width="1.1" stroke-linejoin="round" stroke-linecap="round">
        <path d="M0-15c-8 0-12 6-12 12c0 7 6 11 9 16h6c3-5 9-9 9-16c0-6-4-12-12-12z" fill="var(--paper-elev)"/>
        <path d="M-4.5-14c-3.5 7-3.5 20 0 27M4.5-14c3.5 7 3.5 20 0 27" fill="none"/>
        <path d="M-4.5-14c-3.5 7-3.5 20 0 27h-3.5c-3-5-6-9-6-14c0-6 4-11 9.5-13z" fill="var(--szene-sonne)" fill-opacity="0.6" stroke="none"/>
        <path d="M4.5-14c3.5 7 3.5 20 0 27h3.5c3-5 6-9 6-14c0-6-4-11-9.5-13z" fill="var(--szene-sonne)" fill-opacity="0.6" stroke="none"/>
        <path d="M-3 13l-2.5 7M3 13l2.5 7" stroke-width="0.8" fill="none"/>
        <rect x="-5" y="20" width="10" height="4.5" rx="1.2" fill="var(--paper-elev)"/>
        <path d="M-5 22.2h10" stroke-width="0.8"/>
      </g>`, boden: "" }
    : { himmel: "", boden: SCHMETTERLING };
}

/*
 * Der Schmetterling des Entwurfs (07.10.2026, Martin: „Wow, sieht ganz toll
 * aus"): zwei runde, helle Flügel mit einem goldenen Punkt, ein dunkler
 * Leib, zwei Fühler. Kleiner und ruhiger als der alte (26 → 15 Einheiten).
 *
 * ⚠️ 07.09.2026 — die Punkte sitzen IM Flügel, nicht daneben. Martin: „Die
 *    zwei Punkte auf dem Flügel bleiben dann außerhalb vom Schmetterling,
 *    wenn er zusammenfaltet." Ein Flügel ist eine Gruppe, kein Pfad.
 * ⚠️ Kein `--szene-sonne` im Boden (die Sonne gehört in die Ebene, die nicht
 *    gezogen wird — Wächter in tagesszene-zeit): der Punkt trägt den Mond-Ton.
 */
const SCHMETTERLING = `<g class="szene-falter">
        <g class="szene-fluegel-l">
          <ellipse cx="-3.6" cy="-0.6" rx="3.8" ry="4.6" fill="var(--szene-falter)" stroke="var(--szene-falter-strich)" stroke-width="0.6"/>
          <circle cx="-4" cy="-1" r="0.9" fill="var(--szene-mond)"/>
        </g>
        <g class="szene-fluegel-r">
          <ellipse cx="3.6" cy="-0.6" rx="3.8" ry="4.6" fill="var(--szene-falter)" stroke="var(--szene-falter-strich)" stroke-width="0.6"/>
          <circle cx="4" cy="-1" r="0.9" fill="var(--szene-mond)"/>
        </g>
        <rect x="-0.55" y="-3.6" width="1.1" height="7.2" rx="0.55" fill="var(--szene-falter-strich)"/>
        <path d="M-0.3-3.6L-2-5.8M0.3-3.6L2-5.8" stroke="var(--szene-falter-strich)" stroke-width="0.45" stroke-linecap="round"/>
      </g>`;

/**
 * Glühwürmchen — jede Nacht. (05.09.2026 spät, Martin: „es bewegt sich
 * wieder nichts") Gemessen: nachts liefen alle Animationen, aber die Wolken
 * ziehen mit 0,05 Pixel je Sekunde, Vögel gibt es nur am Tag, und die
 * Sternschnuppe ist 0,7 s je Minute sichtbar. Eine Nacht braucht etwas, das
 * immer lebt — leise, am Rand der Wiese.
 */
function gluehwuermchen(streckung = 1, jz: Jahreszeit = "sommer"): string {
  // 07.10.2026: keine Glühwürmchen im Winter — dort bewegt sich nachts der
  // Schnee, und ein Leuchtkäfer im Schnee wäre ein Fehler im Bild.
  if (jz === "winter") return "";
  /*
   * 01.10.2026 (Ticket 2892): jedes mit einem zarten Hof, wie im Entwurf, und
   * im Boden-Bild aufrecht gehalten (`scale(1/f 1)`) — ein gezogener
   * Leuchtpunkt waere eine Ellipse. Die Bewegung (Gluehen und ein kurzes
   * Treiben) steht im Blatt.
   */
  const f = streckung > 0 ? streckung : 1;
  const sx = Math.round((1 / f) * 1000) / 1000;
  const wurm = (x: number, y: number, r: number, klasse = ""): string =>
    `<g transform="translate(${x} ${y}) scale(${sx} 1)"><g class="szene-gluehwurm${klasse}">` +
    `<circle cx="0" cy="0" r="${r}"/><circle cx="0" cy="0" r="${(r * 2.6).toFixed(1)}" fill-opacity="0.22"/></g></g>`;
  // 07.10.2026: die Stellen des Entwurfs — über der Wiese, nicht mehr im
  // linken Baum (er steht seither bei x = 62).
  return `<g fill="var(--szene-mond)">
        ${wurm(130, 100, 1.2)}
        ${wurm(252, 94, 1.2, " szene-gluehwurm--zwei")}
        ${wurm(282, 122, 1.1, " szene-gluehwurm--drei")}
        ${wurm(150, 126, 1.1, " szene-gluehwurm--zwei")}
      </g>`;
}

/**
 * Der Sternenhimmel. (06.09.2026, zweiter Anlauf)
 *
 * Martin zum ersten Versuch: *„Die Sterne schauen ja furchtbar aus, um Gottes
 * willen, was soll das sein? Komplett verzerrt, schrecklich. Schauen doch
 * keine Sterne aus. Viel zarter, und es muss nicht unbedingt gezeichnet sein,
 * wie der Große Wagen aussieht, und die Proportionen müssen auch stimmen."*
 *
 * Er hatte in jedem Punkt recht, und die Gruende waren drei:
 *
 *   1. **Sie waren DUNKLER als der Himmel.** Gezeichnet wurde mit `--ink`;
 *      im hellen Modus ist das die dunkle Tinte, und der Nachthimmel ist dort
 *      ein mittleres Grün. Dunkle Punkte auf hellem Grund sind Fliegen, keine
 *      Sterne. Sterne sind LICHT auf dunklem Grund — immer.
 *   2. **Sie waren zu gross.** Bis 5 px Durchmesser auf 390 px Breite.
 *   3. **Die Koordinaten waren erfunden.** Ich hatte die Figuren nach Gefühl
 *      hingesetzt; der Wagen war gestaucht.
 *
 * ## Jetzt: echte Koordinaten
 *
 * Rund um den Himmelspol ist die Polarprojektion die richtige. Der Abstand
 * eines Sterns vom Pol ist (90 Grad minus Deklination), sein Winkel ist die
 * Rektaszension. Damit stimmen die Verhaeltnisse — und genau die erkennt man
 * wieder, nicht die Linien dazwischen.
 *
 * Gerechnet mit Pol bei (196, 40), Massstab 2,3 Bildeinheiten je Grad und
 * einer Drehung um 90 Grad, damit die Achse Wagen–Cassiopeia waagrecht liegt
 * (am Himmel stehen sie einander gegenueber). Skript: scratchpad/sternkarte.mjs.
 *
 * ⚠️ KEINE Verbindungslinien. Martin: „es muss nicht unbedingt gezeichnet
 *    sein, wie der Grosse Wagen aussieht." Ein Sternbild mit Strichen ist
 *    eine Lehrtafel; wer den Wagen kennt, sieht ihn ohnehin.
 *
 * ⚠️ Es bleibt kein Planetarium: Die Himmelsrichtung stimmt nicht. Was
 *    stimmt, sind die Verhaeltnisse untereinander.
 */
interface Stern {
  x: number;
  y: number;
  /** Scheinbare Helligkeit; kleiner ist heller. */
  m: number;
}

/**
 * Grosser Wagen, Cassiopeia und Polaris — aus den echten Koordinaten
 * gerechnet, nicht gesetzt. Die Reihenfolge ist die der Rechnung.
 */
const STERNBILDER: Stern[] = [
  // Grosser Wagen: Kasten (Dubhe, Merak, Phekda, Megrez), dann die Deichsel
  { x: 133.0, y: 24.2, m: 1.79 }, // Dubhe
  { x: 121.2, y: 20.6, m: 2.37 }, // Merak
  { x: 112.5, y: 37.7, m: 2.44 }, // Phekda
  { x: 120.3, y: 45.1, m: 3.31 }, // Megrez
  { x: 119.9, y: 58.3, m: 1.77 }, // Alioth
  { x: 120.7, y: 68.9, m: 2.27 }, // Mizar
  { x: 112.5, y: 82.3, m: 1.86 }, // Alkaid
  // Cassiopeia
  { x: 266.9, y: 37.2, m: 2.27 }, // Caph
  { x: 271.8, y: 26.5, m: 2.24 }, // Schedar
  { x: 261.3, y: 23.5, m: 2.47 }, // Gamma
  { x: 259.7, y: 15.0, m: 2.68 }, // Ruchbah
  { x: 249.2, y: 11.0, m: 3.38 }, // Segin
];
/** Der Pol. Er steht still, waehrend alles andere um ihn wandert. */
const POLARIS: Stern = { x: 197.3, y: 39.0, m: 1.98 };
/**
 * Der volle Himmel. (06.09.2026, dritter Anlauf)
 *
 * Martin: *„Da gehören viel mehr Sterne. Da sind hier gar keine Sterne. Der
 * Himmel soll voll sein, so wie er es ist, und man muss es nicht unbedingt
 * sehen."*
 *
 * Er hat recht: 25 Punkte sind kein Nachthimmel. Ein echter hat Hunderte,
 * und die Bilder darin erkennt man nur, wenn man sie sucht — genau das ist
 * der Reiz. Also ein dichtes Feld, in das die gerechneten Sternbilder
 * eingebettet sind statt allein dazustehen.
 *
 * ⚠️ DETERMINISTISCH. Kein `Math.random()` beim Zeichnen: Die Szene wird
 *    stuendlich neu gebaut, und ein Himmel, dessen Sterne dabei springen,
 *    waere Unruhe statt Nacht. Der Generator hier liefert bei gleichem
 *    Startwert immer dasselbe Feld.
 *
 * ⚠️ Die Verteilung ist nicht gleichmaessig, sondern zur Mitte hin etwas
 *    dichter und nach unten hin duenner — am Horizont steht Dunst, und ein
 *    Himmel mit gleichverteilten Punkten sieht aus wie Millimeterpapier.
 */
/**
 * Wie weit das Feld um Polaris reicht.
 *
 * Gerechnet, nicht geraten: der Himmel dreht sich um den Pol, und wo das
 * Feld endet, entstuende beim Drehen ein leeres Eck. Der weiteste Punkt des
 * Bildes vom Pol (197,3 | 39) ist die Ecke unten links — 209,8 Einheiten.
 * Mit etwas Rand: 214.
 */
const HIMMEL_R = 214;

function feldSterne(): Stern[] {
  // Kleiner, schneller Generator mit festem Startwert (xorshift32).
  let z = 0x9e3779b9;
  const zufall = (): number => {
    z ^= z << 13;
    z ^= z >>> 17;
    z ^= z << 5;
    return ((z >>> 0) % 100000) / 100000;
  };
  const out: Stern[] = [];
  /*
   * ⚠️ 06.09.2026 — das Feld liegt jetzt auf einer SCHEIBE um Polaris, nicht
   *    mehr im Rechteck des Bildes. Martin: *„Bewegt sich eigentlich der
   *    Sternenhimmel auch mit? Der halt extrem langsam."* Er tut es seither.
   *    Ein Feld im Rechteck haette beim Drehen leere Ecken hinterlassen.
   *
   * Gleichverteilt auf der Flaeche (Wurzel des Zufallswerts fuer den
   * Halbmesser) — ohne die Wurzel draengten sich alle Sterne in der Mitte.
   */
  const flaeche = Math.PI * HIMMEL_R * HIMMEL_R;
  const anzahl = Math.round(flaeche / 286); // dieselbe Dichte wie bisher
  for (let i = 0; i < anzahl; i++) {
    const w = zufall() * Math.PI * 2;
    const r = Math.sqrt(zufall()) * HIMMEL_R;
    // Je schwaecher, desto haeufiger: so sieht ein echter Himmel aus.
    const m = 3.6 + zufall() * 2.2;
    out.push({
      x: Math.round((POLARIS.x + Math.cos(w) * r) * 10) / 10,
      y: Math.round((POLARIS.y + Math.sin(w) * r) * 10) / 10,
      m,
    });
  }
  return out;
}

const STREUSTERNE: Stern[] = feldSterne();

/**
 * Wie weit sich der Himmel seit dem Bezugszeitpunkt gedreht hat, in Grad.
 * (06.09.2026)
 *
 * Martin: *„Bewegt sich eigentlich der Sternenhimmel auch mit? Der halt
 * extrem langsam."*
 *
 * Ja — und zwar mit dem einzigen Takt, den es dafuer gibt: einmal ganz herum
 * je STERNTAG, also 23 Stunden 56 Minuten 4 Sekunden. Das sind 0,2507 Grad
 * je Minute. Man sieht es beim Hinsehen nicht; wer nach zwei Stunden
 * wiederkommt, findet den Wagen dreissig Grad weiter.
 *
 * ⚠️ Das Vorzeichen ist negativ, weil in SVG die y-Achse nach UNTEN zeigt:
 *    ein positiver Winkel drehte im Bild rechtsherum. Am Nordhimmel wandern
 *    die Sterne linksherum um den Pol.
 *
 * ⚠️ Der Bezugspunkt ist der Abend, an dem Martin das Bild abgenommen hat
 *    („Sieht viel besser aus"). So steht der Himmel um diese Uhrzeit
 *    weiterhin genau so, wie er ihn kennt, und wandert von dort aus.
 */
const STERNTAG_MS = 86_164_091;
const HIMMEL_BEZUG_MS = Date.UTC(2026, 8, 6, 20, 0, 0); // 06.09.2026, 22:00 MESZ

export function sternenWinkel(jetzt: Date): number {
  const seit = jetzt.getTime() - HIMMEL_BEZUG_MS;
  const grad = -((seit / STERNTAG_MS) * 360);
  // Auf 0..360 bringen, damit die Zahl im Markup kurz bleibt.
  return Math.round((((grad % 360) + 360) % 360) * 100) / 100;
}

/** Einen Stern um Polaris drehen. */
function drehe(s: Stern, grad: number): Stern {
  if (grad === 0) return s;
  const w = (grad * Math.PI) / 180;
  const co = Math.cos(w);
  const si = Math.sin(w);
  const dx = s.x - POLARIS.x;
  const dy = s.y - POLARIS.y;
  return {
    x: Math.round((POLARIS.x + dx * co - dy * si) * 10) / 10,
    y: Math.round((POLARIS.y + dx * si + dy * co) * 10) / 10,
    m: s.m,
  };
}

/**
 * Aus der Helligkeit wird ein Radius.
 *
 * ⚠️ Klein. Der hellste Stern misst 1,3 Einheiten im Halbmesser, der
 *    schwaechste 0,4. Vorher waren es 2,5 — und 5 px Durchmesser sind auf
 *    390 px Breite keine Sterne mehr, sondern Punkte.
 */
function sternGroesse(m: number): number {
  return Math.max(0.4, 1.3 - (m - 1.77) * 0.26);
}

/**
 * Bis zu welcher Helligkeit man bei dieser Bewoelkung noch Sterne sieht.
 * (06.09.2026)
 *
 * Martin: *„Wichtig ist natuerlich, wenn es bewoelkt ist, dass man dann
 * Cloudy sieht, aber man muss sich ueberlegen, wie man das mit den Wolken
 * macht."*
 *
 * Vorher war es ein Schalter: Bei allem ausser klarem Himmel fielen genau
 * die Sterne weg, die hinter DER einen Wolke lagen — bei 20 Prozent
 * Bewoelkung dieselben wie bei 95. Das ist nicht, wie ein Himmel sich
 * verhaelt.
 *
 * Jetzt zwei Dinge zusammen: Was hinter einer Wolke liegt, ist weg (das
 * blieb), UND je mehr Himmel bedeckt ist, desto weniger schwache Sterne
 * bleiben ueberhaupt uebrig. Bei bedecktem Himmel sieht man keinen einzigen
 * — dann traegt die Wolke das Bild, und genau das soll man sehen.
 *
 * Die Schwellen folgen der Erfahrung: Die hellsten Sterne stehen bis etwa
 * Groesse 2,5 und halten sich am laengsten; alles ab 4 verschwindet schon
 * bei duennem Schleier.
 */
export function sterngrenze(bewoelkung?: number | null): number {
  if (typeof bewoelkung !== "number" || !Number.isFinite(bewoelkung)) return 9;
  const p = Math.max(0, Math.min(100, bewoelkung));
  /*
   * ⚠️ 06.09.2026, zweiter Anlauf: Die erste Fassung war viel zu streng.
   *    Schon bei leichter Bewoelkung fielen alle schwachen Sterne weg, und
   *    weil der Wolkenkasten ausgerechnet ueber dem Grossen Wagen liegt,
   *    blieb am Ende nur Cassiopeia stehen. Martin: „abgesehen davon, dass
   *    er ueberhaupt nicht mehr sichtbar ist, sieht man nur mehr Cassiopeia."
   *
   *    Jetzt bleibt der Himmel lange voll. Erst ab starker Bewoelkung duennt
   *    er merklich aus, und ganz leer ist er nur bei wirklich bedecktem
   *    Himmel — dann traegt die Wolke das Bild.
   */
  /*
   * Die Stufen sind an der Verteilung des Feldes gemessen (Groessen 3,6 bis
   * 5,8): 5,2 laesst rund drei Viertel stehen, 4,6 knapp die Haelfte, 4,0
   * ein Fuenftel. Bei 3,4 bleiben nur noch die Sternbilder, die alle heller
   * als 3,4 sind — der Himmel ist dann fast leer, aber nicht tot.
   */
  /*
   * ⚠️ 06.09.2026, dritter Anlauf. Auch die zweite Rampe war noch eine
   *    Klippe: bei 84 Prozent standen 25 Sterne, bei 86 nur noch 7. Martin
   *    sah genau diesen Sprung („der schoene Sternenhimmel ... wieso ist das
   *    ploetzlich wieder weg?"). Das Ausduennen nach Helligkeit darf gar
   *    nicht mehr die Hauptarbeit machen — dafuer gibt es jetzt den
   *    Schleier, der stetig ist. Hier bleibt nur ein leichtes Lichten.
   */
  if (p < 40) return 9;    // klar bis heiter: alle
  if (p < 60) return 5.2;  // ein paar Wolken: die schwaechsten gehen
  if (p < 78) return 4.9;  // halb bedeckt
  return 4.6;              // bedeckt: gelichtet, den Rest macht der Schleier
}

/**
 * Wie klar der Himmel durchkommt — 1 heisst voll, 0,3 heisst kaum. (06.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, mit Bild, um 22:02: *„Jetzt wird ploetzlich der schoene
 * Sternenhimmel ueberblendet von einem alten Bild. Wieso ist das ploetzlich
 * wieder weg? Und der Mond ist auch verschwunden."*
 *
 * Gemessen: Elf Minuten vorher stand der volle Himmel da. Dazwischen ist
 * nichts am Code passiert — die Bewoelkung stieg von unter 85 auf **86
 * Prozent**. Genau dort stand eine Klippe: `gestirnSichtbar` gab ab 85 false
 * zurueck und der Mond wurde gar nicht mehr gezeichnet, `sterngrenze` gab ab
 * 93 die Null. EIN Prozentpunkt loeschte das halbe Bild.
 *
 * ⚠️ Das war der Denkfehler: Bewoelkung ist ein stetiger Wert, und ein
 *    stetiger Wert darf keine Klippe steuern. Ein bedeckter Himmel loescht
 *    den Mond auch draussen nicht — er verschleiert ihn zu einem hellen
 *    Fleck. Genau das macht diese Zahl.
 *
 * Unter 85 Prozent aendert sie nichts (der Himmel war dort nie das Problem).
 * Darueber sinkt sie gleichmaessig bis 0,3 — sichtbar, aber zurueckgenommen.
 */
export function himmelSchleier(bewoelkung?: number | null): number {
  if (typeof bewoelkung !== "number" || !Number.isFinite(bewoelkung)) return 1;
  const p = Math.max(0, Math.min(100, bewoelkung));
  if (p <= 85) return 1;
  return Number((1 - ((p - 85) / 15) * 0.7).toFixed(3));
}

/**
 * Wieviel Rand ueber das Bild hinaus mitgezeichnet wird.
 *
 * Die Szene wird nur STUENDLICH neu gebaut; dazwischen dreht `rueckeGestirn`
 * die Gruppe jede Minute weiter. In einer Stunde dreht sich der Himmel 15
 * Grad — ein Stern am aeusseren Rand (214 Einheiten) legt dabei bis zu 56
 * Einheiten zurueck. Mit 60 Einheiten Rand ist beim naechsten Neubau nie
 * ein leeres Eck entstanden.
 */
const HIMMEL_RAND = 60;

function sterne(
  verdeckt: boolean,
  bewoelkung?: number | null,
  schleier = 1,
  jetzt: Date = new Date(),
): string {
  const k = WOLKE_KASTEN;
  const grenze = sterngrenze(bewoelkung);
  const winkel = sternenWinkel(jetzt);
  const imBild = (s: Stern): boolean =>
    s.x >= -HIMMEL_RAND &&
    s.x <= 390 + HIMMEL_RAND &&
    s.y >= -HIMMEL_RAND &&
    s.y <= 108 + HIMMEL_RAND;
  const frei = (s: Stern): boolean =>
    s.m <= grenze &&
    (!verdeckt || !(s.x >= k.x1 && s.x <= k.x2 && s.y >= k.y1 && s.y <= k.y2));
  /*
   * ⚠️ Erst DREHEN, dann pruefen. Der Dunst ueber dem Horizont und der
   *    Wolkenkasten haengen daran, wo ein Stern JETZT steht — nicht daran,
   *    wo er im ungedrehten Feld liegt. Andersherum waere der Dunst eine
   *    Eigenschaft des Sterns statt des Himmels und wuerde mitwandern.
   */
  const alle = [...STERNBILDER, POLARIS, ...STREUSTERNE]
    .map((s) => drehe(s, winkel))
    .filter(imBild)
    // Nach unten hin duenner — Dunst ueber dem Horizont.
    .filter((s) => s.y <= 78 || ((s.x * 7 + s.y * 13) | 0) % 100 < 45)
    .filter(frei);
  const polarJetzt = POLARIS; // steht still, er IST die Achse
  if (alle.length === 0) return "";
  const punkte = alle
    .map((s, i) => {
      const r = sternGroesse(s.m).toFixed(2);
      // Jeder funkelt in eigenem Takt — sonst blinkt der Himmel im Chor.
      const takt = (4.2 + ((i * 7) % 13) * 0.36).toFixed(2);
      const versatz = (((i * 11) % 19) * 0.31).toFixed(2);
      return `<circle class="szene-stern" cx="${s.x}" cy="${s.y}" r="${r}"
        style="--funkel:${takt}s;--funkel-start:-${versatz}s"/>`;
    })
    .join("");
  /*
   * Der Polarstern bekommt einen Hauch Schein — er ist der einzige, der
   * stillsteht, und der einzige, den jemand sucht.
   */
  const polar = frei(polarJetzt)
    ? `<circle class="szene-polarschein" cx="${polarJetzt.x}" cy="${polarJetzt.y}" r="3"/>`
    : "";
  // Ein Wert auf der GRUPPE statt an jedem Stern: die Funkel-Animation
  // veraendert die Deckkraft der einzelnen Kreise und wuerde ihn sonst
  // jedes Mal ueberschreiben.
  /*
   * Ein Glitzern auf den hellsten drei Sternen. (01.10.2026, Ticket 2892)
   * Ein feines Kreuz, das nur selten aufblitzt (CSS: 17 s Umlauf, wenige
   * Prozent sichtbar). Es sitzt in der Sterngruppe und dreht mit.
   */
  const glitzer = alle
    .filter((s) => s.m < 1.9)
    .slice(0, 3)
    .map(
      (s, i) =>
        `<path class="szene-glitzer szene-glitzer--${i}" d="M${s.x} ${s.y - 4.2}V${s.y + 4.2}M${s.x - 4.2} ${s.y}H${s.x + 4.2}" stroke="var(--szene-stern-farbe, #fff)" stroke-width="0.6" stroke-linecap="round"/>`,
    )
    .join("");
  const nebel = schleier < 1 ? ` style="opacity:${schleier}"` : "";
  /*
   * `data-winkel` merkt sich, bei welchem Stand gezeichnet wurde. Die
   * Minutenuhr (rueckeGestirn) dreht von dort aus weiter, statt das ganze
   * Feld neu zu setzen — sonst begaenne bei jedem Takt das Funkeln von vorn.
   */
  return `<g class="szene-sterne" data-winkel="${winkel}"${nebel}>${polar}${punkte}${glitzer}</g>`;
}

/**
 * Ist überhaupt ein Gestirn zu sehen? (05.09.2026)
 *
 * Martin: *„Regenschauer und Sonnenschein ist ein Widerspruch … wenn stark
 * bewölkt ist, vielleicht dass man die Sonne dann ganz weggibt, und beim
 * Regen sowieso."*
 *
 * Er hat recht, und es war ein echter Fehler im Bild: Die Sonne wurde
 * tagsüber IMMER gezeichnet, ganz gleich, was das Wetter meldete. Bei Regen
 * stand also eine Sonne über den Regenzügen.
 *
 * ⚠️ Die Schwelle ist 85 Prozent, nicht 100. Bei „bedeckt" (über 85) ist die
 *    Sonne wirklich nicht zu sehen; bei 70 Prozent scheint sie zwischen den
 *    Wolken durch, und ein leerer Himmel wäre dann genauso falsch wie vorher
 *    die Sonne im Regen.
 *
 * ⚠️ Gilt auch für den Mond. Eine bedeckte Nacht zeigt keinen Mond — und die
 *    Sterne waren schon vorher an die Lage gekoppelt.
 */
export function gestirnSichtbar(
  lage: Wetterlage,
  bewoelkung?: number | null,
): boolean {
  if (lage === "regen") return false;
  if (typeof bewoelkung === "number" && Number.isFinite(bewoelkung)) {
    return bewoelkung < 85;
  }
  // Ohne Messwert entscheidet die grobe Lage — „wolken" allein heisst noch
  // nicht bedeckt.
  return true;
}

function gestirn(
  zeit: Tageszeit,
  lage: Wetterlage,
  jetzt: Date,
  sonne?: Partial<Sonnenzeiten>,
  bewoelkung?: number | null,
): string {
  /*
   * ⚠️ Die Nacht wird ZUERST entschieden und folgt einer anderen Regel als
   *    der Tag. Beim Tag hat Martin die Sonne bei starker Bewoelkung
   *    ausdruecklich weghaben wollen („dass man die Sonne dann ganz
   *    weggibt"); beim Mond hat er das Gegenteil verlangt, als er ihn
   *    verschwinden sah. Beides in EINER Schwelle unterzubringen war der
   *    Fehler — siehe himmelSchleier.
   */
  if (zeit === "nacht") return nachthimmel(lage, jetzt, sonne, bewoelkung);
  if (!gestirnSichtbar(lage, bewoelkung)) return "";
  return sonnenhimmel(zeit, jetzt, sonne);
}

/** Der Nachthimmel: Sterne immer, Mond ausser bei Regen — beide verschleiert. */
function nachthimmel(
  lage: Wetterlage,
  jetzt: Date,
  sonne?: Partial<Sonnenzeiten>,
  bewoelkung?: number | null,
): string {
  const schleier = himmelSchleier(bewoelkung);
  // Bei Regen sieht man den Mond wirklich nicht. Das bleibt die einzige
  // Lage, in der er ganz fehlt — dann brauchen die Sterne auch keine Luecke.
  if (lage === "regen") {
    // `verdeckt` = true: bei Regen liegt die Wolke ueber dem Sternenkasten.
    return sterne(true, bewoelkung, schleier, jetzt);
  }
  {
    /*
     * ⚠️ 2026-09-03/04 — zwei Wuensche von Martin in einem:
     * „der Mond koennte gelb sein, romantifiziert" und „das Gleiche fuer den
     * Mond: dass auch die Sichel in der richtigen Position ist."
     *
     * `--accent` war das Gruen der Baeume; `--ember` scheidet aus, weil er
     * mit der Palette wandert (Wald dunkel: #6fbf86). Deshalb `--szene-mond`.
     * Die Sichel kommt jetzt aus der ECHTEN Phase, die Stellung aus der Bahn.
     */
    const b = bahnPunkt(bahnFortschritt(jetzt, sonne, true));
    const r = 21;
    const phase = mondphase(jetzt);
    /*
     * ⚠️ 08.09.2026 — der Mond steht VOR den Sternen, auch wo er dunkel ist.
     *
     * Martin: *„Ich sehe noch einen Bug im Mond, weil der Mond vor den
     * Sternen sein muss, auch wenn er nicht ganz ist."*
     *
     * Er hat recht, und zwar physikalisch: Der Mond ist ein Koerper, kein
     * Licht. Durch seine unbeleuchtete Seite sieht man keinen Stern.
     *
     * Die Reihenfolge stimmte schon — die Mondgruppe wird NACH den Sternen
     * gezeichnet. Nur ist die Scheibe durchsichtig (fill-opacity 0.07 fuer
     * die dunkle Seite, 0.55 fuer die Sichel), und durch Durchsichtiges
     * scheint eben durch.
     *
     * ⚠️ Darum eine Maske statt einer deckenden Fuellung: Der Himmel ist ein
     *    Verlauf; eine flache Fuellung in „Himmelfarbe" waere an jeder
     *    Bahnstelle ein anderer Fleck. Die Maske schneidet die Sterne dort
     *    einfach weg — die Optik des Mondes bleibt, wie sie ist.
     */
    const loch = `mond-loch-${Math.round(b.x)}-${Math.round(b.y)}`;
    const sternenfeld =
      `<defs><mask id="${loch}">` +
      `<rect x="-40" y="-40" width="480" height="200" fill="white"/>` +
      `<circle cx="${b.x}" cy="${b.y}" r="${r}" fill="black"/>` +
      `</mask></defs>` +
      `<g mask="url(#${loch})">` +
      sterne(lage !== "sonne", bewoelkung, schleier, jetzt) +
      `</g>`;
    // Der Schleier liegt auf der GRUPPE, nicht an sechs einzelnen Werten —
    // eine Zahl statt sechs, die auseinanderlaufen koennen.
    const huelle = schleier < 1 ? ` style="opacity:${schleier}"` : "";
    return `${sternenfeld}${bahnSpur()}<g class="szene-mond-gruppe"${huelle}>
      <defs>
        <radialGradient id="szene-mondschein">
          <stop offset="0%" stop-color="var(--szene-mond)" stop-opacity="0.20"/>
          <stop offset="35%" stop-color="var(--szene-mond)" stop-opacity="0.11"/>
          <stop offset="65%" stop-color="var(--szene-mond)" stop-opacity="0.04"/>
          <stop offset="100%" stop-color="var(--szene-mond)" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="${b.x}" cy="${b.y}" r="${r + 25}" class="szene-mondschein-kreis" fill="url(#szene-mondschein)"/>
      ${/*
        05.09.2026, Martin: „Ist es vielleicht schöner, dass der Mond
        tatsächlich eine Sichel ist, ohne diesen Rand?" — Ja. Eine gezeichnete
        Kontur macht aus der Sichel einen Kreis mit Füllung. Was am echten
        Himmel die dunkle Seite verrät, ist das Erdlicht: ganz leise, ohne
        Linie. So bleibt die wahre Größe lesbar, und die Sichel bleibt Sichel.
      */ ""}
      <circle cx="${b.x}" cy="${b.y}" r="${r}" fill="var(--szene-mond)" fill-opacity="0.07" stroke="none" class="szene-mondscheibe"/>
      <path d="${mondPfad(b.x, b.y, r, phase)}"
        fill="var(--szene-mond)" fill-opacity="0.55"
        stroke="var(--szene-mond)" stroke-width="1.2" stroke-opacity="0.8" class="szene-mondsichel"/>
      ${/*
        01.10.2026 (Ticket 2892, „Mond mit Glut"): dieselbe Form noch einmal,
        breit und zart in der Sonnenfarbe — der warme Rand einer tiefstehenden
        Sichel. Gleiche Klasse wie die Sichel, damit `rueckeGestirn` beide
        zusammen fuehrt.
      */ ""}
      <path d="${mondPfad(b.x, b.y, r, phase)}" class="szene-mondsichel szene-mondglut"
        stroke="var(--szene-sonne)" stroke-width="4.5" stroke-opacity="0.16" stroke-linejoin="round"/>
      </g>${sternschnuppen(jetzt)}`;
  }
}

/** Der Taghimmel: die Sonne auf ihrer Bahn. */
function sonnenhimmel(
  zeit: Tageszeit,
  jetzt: Date,
  sonne?: Partial<Sonnenzeiten>,
): string {
  {
  /*
   * ⚠️ 2026-09-04 (Martin: „waer's nicht schoener, wenn die Sonne wirklich im
   * Osten aufgeht und im Westen runter?"): Vorher DREI feste Punkte, alle in
   * der rechten Bildhaelfte — sie sprang, statt zu wandern. Jetzt ein Bogen
   * ueber die ganze Breite, gespeist aus den echten Sonnenzeiten.
   */
  const sp = sonnenPunkt(jetzt, sonne);
  const b = { x: sp.x, y: sp.scheibeY };
  const r = zeit === "mittag" ? 20 : 23;
  const schein = r + 26;
  /*
   * ⚠️ 01.10.2026 (Ticket 2892, „Sonne auf das naechste Level") — drei
   * Schichten statt einer Scheibe mit Kranz:
   *   SCHEIN   der weite Hof (bestand), jetzt mit hellerem Kern
   *   KORONA   ein weicher Ring knapp um die Scheibe — er „atmet" (CSS)
   *   KERN     ueber der Scheibe: oben links ein Glanzpunkt, zum Rand waermer
   * Alles in Tokens (`--szene-sonne`, `--szene-sonne-glanz`), keine Festfarbe.
   * `rueckeGestirn` setzt cx/cy jeder dieser Schichten mit.
   *
   * 07.10.2026 (Entwurf „ruhiger", Martin: „Wow, sieht ganz toll aus"): die
   * Scheibe ist jetzt GEFÜLLT statt ein Ring, die Strahlen leiser (0,3), und
   * der weite Schein atmet (CSS, 9 s) — eine warme Sonne statt eines Zeichens.
   */
  return `${bahnSpur()}<defs>
      <radialGradient id="szene-schein">
        <stop offset="0%" stop-color="var(--szene-sonne)" stop-opacity="0.38"/>
        <stop offset="30%" stop-color="var(--szene-sonne)" stop-opacity="0.2"/>
        <stop offset="65%" stop-color="var(--szene-sonne)" stop-opacity="0.06"/>
        <stop offset="100%" stop-color="var(--szene-sonne)" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="szene-sonnenkern" cx="0.38" cy="0.34" r="0.75">
        <stop offset="0%" stop-color="var(--szene-sonne-glanz)" stop-opacity="0.9"/>
        <stop offset="35%" stop-color="var(--szene-sonne)" stop-opacity="0.45"/>
        <stop offset="100%" stop-color="var(--szene-sonne)" stop-opacity="0.22"/>
      </radialGradient>
    </defs>
    <circle cx="${sp.x}" cy="${sp.y}" r="${schein}" class="szene-schein" fill="url(#szene-schein)"/>
    <circle cx="${b.x}" cy="${b.y}" r="${r + 7}" class="szene-korona"
      stroke="var(--szene-sonne)" stroke-width="6" stroke-opacity="0.11"/>
    <circle cx="${b.x}" cy="${b.y}" r="${r}"
      fill="var(--szene-sonne)" fill-opacity="0.9"
      stroke="var(--szene-sonne)" stroke-width="1.6" stroke-opacity="0.35" class="szene-sonnenscheibe"/>
    <circle cx="${b.x}" cy="${b.y}" r="${r - 1}" class="szene-sonnenkern" fill="url(#szene-sonnenkern)"/>
    ${strahlen(b.x, b.y, r)}`;
  }
}

/**
 * Die Strahlen — gerechnet statt gezeichnet.
 *
 * ⚠️ Vorher standen acht Pfade mit festen Zahlen im Markup; sie hingen an
 * `cx=300, cy=52`. Sobald die Sonne wandert, muss jeder einzelne mitwandern,
 * und acht Zahlenpaare von Hand nachzuziehen geht beim ersten Mal schief.
 */
function strahlenPfad(cx: number, cy: number, r: number): string {
  const innen = r + 7;
  const pfade: string[] = [];
  for (let i = 0; i < 8; i++) {
    const w = (i * Math.PI) / 4;
    const dx = Math.cos(w);
    const dy = Math.sin(w);
    // 01.10.2026: lange und kurze im Wechsel — ein gleichmaessiger Kranz
    // liest sich als Zahnrad, ein ungleicher als Licht.
    const aussen = r + (i % 2 === 0 ? 17 : 12);
    pfade.push(
      `M${(cx + dx * innen).toFixed(1)} ${(cy + dy * innen).toFixed(1)}` +
        `L${(cx + dx * aussen).toFixed(1)} ${(cy + dy * aussen).toFixed(1)}`,
    );
  }
  return pfade.join("");
}

function strahlen(cx: number, cy: number, r: number): string {
  return `<g class="szene-strahlen" stroke="var(--szene-sonne)" stroke-width="1.6" stroke-linecap="round" opacity="0.3">
    <path d="${strahlenPfad(cx, cy, r)}"/>
  </g>`;
}

/**
 * Das Gestirn eine Minute weiterruecken — OHNE die Szene neu zu zeichnen.
 * (06.09.2026, Martin: „weder die Sonne noch die Wolken bewegen sich.")
 *
 * Die Szene wird nur einmal pro Stunde neu gebaut (fuelleSzene-Schluessel),
 * damit Wolken, Voegel und Ueberraschungen ihren Lauf behalten. Die Sonne
 * stand darum bis zu einer Stunde still und sprang dann. Jetzt wandert sie
 * jede Minute: nur cx/cy von Schein und Scheibe und der Strahlenpfad werden
 * gesetzt, der Rest des Himmels bleibt unberuehrt. Nachts dasselbe fuer
 * den Mond. Gibt false zurueck, wenn kein Gestirn im Bild ist.
 */
export function rueckeGestirn(
  halter: ParentNode,
  jetzt: Date,
  sonne?: Partial<Sonnenzeiten>,
  zeit?: Tageszeit,
): boolean {
  const nacht = zeit === "nacht";
  const sp = nacht ? null : sonnenPunkt(jetzt, sonne);
  // Nacht: der Mond auf seiner Bahn. Tag: Scheibe, Korona und Kern (scheibeY); der Schein hat seine eigene Höhe.
  const b = sp ? { x: sp.x, y: sp.scheibeY } : bahnPunkt(bahnFortschritt(jetzt, sonne, true));
  // 02.10.2026: Das Licht im Haus geht mit der Minute aus und an, nicht erst
  // beim stündlichen Neuaufbau — siehe wachAnteil.
  stelleHauslicht(halter, jetzt);
  // Jede Schicht des Gestirns (Schein, Korona, Scheibe) traegt cx/cy — alle
  // gemeinsam setzen, sonst bliebe eine Schicht stehen.
  const setz = (wahl: string): boolean => {
    const els = halter.querySelectorAll(wahl);
    els.forEach((el) => {
      el.setAttribute("cx", String(b.x));
      el.setAttribute("cy", String(b.y));
    });
    return els.length > 0;
  };
  if (nacht) {
    /*
     * 06.09.2026 — der Himmel dreht sich mit. Martin: *„Bewegt sich
     * eigentlich der Sternenhimmel auch mit? Der halt extrem langsam."*
     *
     * Gedreht wird die GRUPPE, nicht jeder Stern: ein Attribut je Minute
     * statt vierhundert. Und relativ zum Stand beim Zeichnen, damit das
     * Funkeln nicht bei jedem Takt von vorn beginnt.
     */
    const feld = halter.querySelector(".szene-sterne");
    if (feld) {
      const gezeichnet = Number(feld.getAttribute("data-winkel") ?? "0");
      const seither = sternenWinkel(jetzt) - gezeichnet;
      feld.setAttribute(
        "transform",
        `rotate(${seither.toFixed(2)} ${POLARIS.x} ${POLARIS.y})`,
      );
    }
    const scheibe = halter.querySelector(".szene-mondscheibe");
    const ok = setz(".szene-mondschein-kreis") && setz(".szene-mondscheibe");
    if (ok && scheibe) {
      const d = mondPfad(b.x, b.y, Number(scheibe.getAttribute("r")), mondphase(jetzt));
      // Sichel UND Glut-Kontur (beide `.szene-mondsichel`).
      halter.querySelectorAll(".szene-mondsichel").forEach((sichel) => sichel.setAttribute("d", d));
    }
    return ok;
  }
  const scheibe = halter.querySelector(".szene-sonnenscheibe");
  const scheinEls = halter.querySelectorAll(".szene-schein");
  scheinEls.forEach((el) => {
    el.setAttribute("cx", String(sp!.x));
    el.setAttribute("cy", String(sp!.y));
  });
  const ok = scheinEls.length > 0 && setz(".szene-sonnenscheibe");
  setz(".szene-korona");
  setz(".szene-sonnenkern");
  const strahl = halter.querySelector(".szene-strahlen path");
  if (ok && strahl && scheibe) {
    strahl.setAttribute("d", strahlenPfad(b.x, b.y, Number(scheibe.getAttribute("r"))));
  }
  return ok;
}

/**
 * Wie stark es regnet — aus den Millimetern des Tages.
 *
 * Martin (11.09.2026): *„Vielleicht waer auch interessant, die Intensitaet
 * des Regens zu definieren."* Die Zahl lag schon in derselben Wetter-Antwort
 * (`heute.regenMm`) und wurde nur nie abgefragt — dieselbe Lage wie bei der
 * Bewoelkung am 05.09.
 *
 * ⚠️ Ohne Zahl bleibt es bei der mittleren Staerke: dem Bild, das es bis
 *    heute gab. Ein fehlender Messwert darf den Himmel nicht leerraeumen.
 */
export function regenStaerke(mm?: number | null): {
  striche: number;
  deckung: number;
  laenge: number;
} {
  const m = typeof mm === "number" && Number.isFinite(mm) ? Math.max(0, mm) : null;
  if (m === null) return { striche: 4, deckung: 0.34, laenge: 13 };
  if (m <= 2) return { striche: 3, deckung: 0.24, laenge: 10 };
  if (m <= 8) return { striche: 4, deckung: 0.34, laenge: 13 };
  if (m <= 15) return { striche: 5, deckung: 0.42, laenge: 16 };
  return { striche: 6, deckung: 0.5, laenge: 19 };
}

/**
 * Regenzüge — nur wenn es wirklich regnet.
 *
 * ## ⚠️⚠️ 11.09.2026 — DER REGEN FIEL NEBEN DIE WOLKE
 *
 * Martin, mit Bild: *„Der Regen passt nicht, weil er nicht unter der Wolke
 * ist."* Er hatte recht, und zwar nachrechenbar. Die Striche standen fest
 * auf x = 68, 86, 104, 122. Die nahe Wolke steht seit der Neuberechnung vom
 * 09.09. bei 100 bis 191 — der Regen fiel also groesstenteils in die Luecke
 * DAVOR (Wolke drei endet bei 63).
 *
 * ⚠️ Die Zahlen hier waren von Hand gesetzt, als die Wolken noch woanders
 *    standen. Beim Umrechnen der Wolken am 09.09. hat niemand daran gedacht
 *    — dieselbe Klasse Fehler wie ueberall: eine Zahl, die von einer anderen
 *    abhaengt, ohne dass der Code das weiss. Jetzt rechnet er sie aus
 *    `WOLKEN_LAGEN` und `WOLKE_MASSE` aus; verschiebt sich die Wolke, kommt
 *    der Regen mit.
 *
 * ⚠️ Und er ZIEHT mit: Die nahe Wolke driftet um ±38 Punkte (420 s). Steht
 *    der Regen still, laeuft sie ihm binnen Minuten davon. Die Klasse
 *    `szene-regen` traegt darum dieselbe Bewegung mit demselben Versatz.
 */
function regen(lage: Wetterlage, regenMm?: number | null): string {
  if (lage !== "regen") return "";
  const w = WOLKEN_LAGEN[0];
  const links = w.x + w.s * WOLKE_MASSE.nah.x;
  const breite = w.s * WOLKE_MASSE.nah.breite;
  const { striche, deckung, laenge } = regenStaerke(regenMm);
  /*
   * ⚠️ Der Regen faellt UNTER der Wolke (ab y=70), nicht hindurch. Der erste
   * Anlauf legte die Striche auf y=38-62 — mitten in die Wolke, wo sie im
   * Browser wie eine Durchstreichung aussahen statt wie Regen.
   */
  const rand = breite * 0.16;
  const feld = breite - 2 * rand;
  const pfad = Array.from({ length: striche }, (_, i) => {
    const x = Math.round(links + rand + (feld * (i + 0.5)) / striche);
    // Versetzte Hoehen — gleich hohe Striche lesen sich als Kamm, nicht als
    // Regen. Dieselbe Ueberlegung wie bei den Wolken: Versatz macht Natur.
    const y = 70 + (i % 2) * 6;
    return `M${x} ${y}l-5 ${laenge}`;
  }).join("");
  return `<g class="szene-regen" stroke="var(--szene-strich)" stroke-width="1.4" stroke-linecap="round" opacity="${deckung}">
    <path d="${pfad}"/>
  </g>`;
}

/** Die Wolke steht bei Regen und Bewölkung; bei Sonne bleibt der Himmel frei. */
/**
 * Wie viele Wolken am Himmel stehen. (05.09.2026)
 *
 * Martin: *„Wenn stark bewölkt ist, dann können natürlich viele Wolken, und
 * wenn wenig bewölkt ist, weniger. Geht das?"*
 *
 * Es ging schon immer — die Zahl lag in derselben Wetter-Antwort und wurde
 * nur nie abgefragt (`cloud_cover`, Prozent). Der Wettercode sagt „bedeckt"
 * oder „heiter", aber nicht, ob das zwei Wolken sind oder acht.
 *
 * ⚠️ Ohne Zahl bleibt es bei zweien — dem Bild, das es bis heute gab. Ein
 *    fehlender Messwert darf den Himmel nicht leerräumen.
 */
export function wolkenZahl(bewoelkung?: number | null): number {
  if (typeof bewoelkung !== "number" || !Number.isFinite(bewoelkung)) return 2;
  const p = Math.max(0, Math.min(100, bewoelkung));
  if (p < 15) return 0;   // klar
  if (p < 40) return 1;   // ein paar Schleier
  if (p < 70) return 2;   // wie bisher
  if (p < 90) return 3;
  return 4;               // bedeckt
}

function wolke(lage: Wetterlage, bewoelkung?: number | null): string {
  if (lage === "sonne") return "";
  /*
   * ⚠️ 2026-09-04 (Martin: „eigentlich müsste die Wolke ja vor der Sonne
   * sein, rein von den Ebenen"):
   *
   * Sie WAR schon die spätere Ebene — nur war sie eine reine KONTUR. Was
   * oben liegt und innen leer ist, verdeckt nichts: die Sonne schien durch
   * ihr Inneres, samt Strahlen. Im Bild sah es aus, als läge sie davor.
   *
   * Dieselbe Lage wie bei den Sternen am 03.09. Dort war die Antwort, die
   * zwei Sterne wegzulassen — die Sonne kann ich nicht weglassen. Also
   * bekommt die Wolke doch eine Füllung.
   *
   * ⚠️ Und zwar KEINE flache: der Himmel ist ein Verlauf, ein einfarbiger
   * Grund hätte eine sichtbare Naht ergeben (genau deshalb hatte ich es am
   * 03.09. abgelehnt). Der Verlauf hier trägt dieselben Token wie der
   * Hintergrund und beginnt 24 Einheiten ÜBER dem Bild — so viel Himmel
   * liegt über der Zeichnung (`--szene-himmel-luft`). Damit trifft die
   * Füllung an jeder Höhe genau den Grund dahinter.
   *
   * ⚠️ Zweite Bitte desselben Tages: „die Wolke ist noch nicht so ganz in
   * Proportion." Sie war 74 breit und 46 hoch (1,6 : 1) — für eine Wolke zu
   * gedrungen, eher ein Ballen. Jetzt rund 100 zu 34 (2,9 : 1), mit drei
   * Ballen statt zweien. Fünf Formen nebeneinander gezeichnet und
   * verglichen; die gewählte hat EINGEZOGENE Ecken an der Unterkante — eine
   * Wolke, die unten quadratisch endet, sieht wie abgeschnitten aus. Jetzt
   * rund 82 zu 30 (2,7 : 1). Wolken sind breiter als hoch.
   *
   * ⚠️ Dazu eine ZWEITE, kleinere und blassere Wolke weiter hinten. Die
   * Huegel tragen ihre Tiefe seit dem 29.08. aus drei gestaffelten Ebenen —
   * am Himmel stand dagegen ein einzelnes Ding in viel Leere. Dieselbe
   * Lehre, dieselbe Loesung: kleiner, hoeher, blasser heisst weiter weg.
   */
  /*
   * ⚠️ 07.10.2026 (Entwurf „ruhiger"): Die Füllung war der Himmel selbst —
   *    eine Kontur mit Himmel darin. Jetzt der eigene Wolkenton, zu einem
   *    Viertel mit dem Himmel derselben Höhe gemischt (`--szene-wolke-oben`
   *    / `-unten`, Blatt). Damit bleibt die Wolke deckend und ohne Naht,
   *    liest sich aber als weiche, helle Form wie im Entwurf; die Kontur
   *    wird dafür leiser (wolkenBild).
   */
  return `<defs>
      <linearGradient id="szene-wolkengrund" gradientUnits="userSpaceOnUse"
        x1="0" y1="-24" x2="0" y2="160">
        <stop offset="0%" stop-color="var(--szene-wolke-oben, var(--paper-soft))"/>
        <stop offset="100%" stop-color="var(--szene-wolke-unten, var(--paper))"/>
      </linearGradient>
    </defs>
    ${wolkenBild(wolkenZahl(bewoelkung))}`;
}

/** Die ferne, kleine Wolkenform. */
const WOLKE_FERN =
  "M156 40a7 7 0 0 1 1-13 10 10 0 0 1 16-7 14 14 0 0 1 22 6 9 9 0 0 1 12 14 " +
  "4 4 0 0 1-4 2h-43a4 4 0 0 1-4-2Z";
/** Die nahe, grosse Wolkenform. */
const WOLKE_NAH =
  "M50 64a11 11 0 0 1 2-20 16 16 0 0 1 25-11 23 23 0 0 1 35 9 15 15 0 0 1 " +
  "20 22 7 7 0 0 1-6 3H56a7 7 0 0 1-6-3Z";

/**
 * So viele Wolken zeichnen, wie der Himmel hergibt.
 *
 * ⚠️ Nur ZWEI Formen, versetzt und skaliert — keine acht Handzeichnungen.
 *    Wolken sehen einander ohnehin ähnlich; was sie unterscheidet, ist
 *    Größe, Höhe und Blässe. Genau das macht auch die Tiefe (`--fern` ist
 *    kleiner, höher und blasser, siehe oben).
 *
 * ⚠️ Der Versatz je Wolke sorgt dafür, dass sie NICHT im Gleichschritt
 *    ziehen. Ohne ihn wäre der Himmel eine Tapete, die sich verschiebt.
 */
/*
 * ⚠️⚠️ 09.09.2026 — DER VERSATZ WAR NOCH IMMER FALSCH GERECHNET.
 *
 * Martin, mit Bild: *„Die Wolkenposition ist nicht korrekt. Zumindest wirkt
 * es so."* Er hatte recht, und zwar nachrechenbar.
 *
 * Der Versatz oben wurde nach Augenmass gesetzt („die Formen sind um x≈50
 * und x≈156 gezeichnet"). Die echten Pfadmasse sind andere — im Browser
 * mit getBBox() gemessen:
 *
 *     WOLKE_NAH   x = 44   Breite 91
 *     WOLKE_FERN  x = 152  Breite 58
 *
 * Und weil `translate(x) scale(s)` erst verschiebt und dann skaliert, gilt
 * fuer die Grundstellung `x + s * pfad_x`. Damit standen die vier so:
 *
 *     W3   16,6 ..  63,0        W1  44,0 .. 135,0   -> 19 Punkte INEINANDER
 *     W2  192,0 .. 250,0        W4 274,2 .. 324,2
 *
 * Wolke 3 lag also dauerhaft zu einem Drittel in Wolke 1 — kein Drift-
 * Zufall, sondern die Grundstellung. Am Telefon las sich der Himmel
 * dadurch als ZWEI Klumpen statt vier Wolken. Im Browser ueber 32 Sekunden
 * nachgemessen: die Ueberlappung blieb durchgehend (21 -> 13 Punkte).
 *
 * Neu gerechnet, nicht geschaetzt:
 *
 *     W3   16,6 ..  63,0   Luecke 37
 *     W1  100,0 .. 191,0   Luecke 27
 *     W2  218,0 .. 276,0   Luecke 24
 *     W4  300,2 .. 350,2   rechts 40 Punkte Rand
 *
 * ⚠️ Die Wolken driften um ±38 Punkte (szene-wolkendrift), jede mit eigener
 *    Dauer und eigenem Versatz. Dass sie sich dabei zeitweise beruehren,
 *    ist gewollt — Wolken tun das. Der Fehler war das STAENDIGE
 *    Uebereinanderliegen. Der rechte Rand von 40 haelt Wolke 4 auch bei
 *    vollem Ausschlag im Bild (Martins Vorgabe vom 05.09.).
 */
/** Pfadmasse, im Browser mit getBBox() gemessen — Grundlage der Rechnung. */
export const WOLKE_MASSE = {
  nah: { x: 44, breite: 91 },
  fern: { x: 152, breite: 58 },
} as const;

export const WOLKEN_LAGEN = [
  { d: "nah", x: 56, y: 0, s: 1, deckung: 0.45, breite: 1.6, klasse: "" },
  { d: "fern", x: 66, y: 0, s: 1, deckung: 0.22, breite: 1.2, klasse: " szene-wolke--fern" },
  { d: "fern", x: -105, y: 30, s: 0.8, deckung: 0.3, breite: 1.2, klasse: " szene-wolke--drei" },
  { d: "nah", x: 276, y: -16, s: 0.55, deckung: 0.18, breite: 1.2, klasse: " szene-wolke--vier" },
] as const;

/**
 * Wie stark die Kontur der Wolke noch mitzeichnet (07.10.2026, Entwurf
 * „ruhiger"): die Füllung trägt die Form jetzt selbst, die Linie bleibt nur
 * als Hauch — 40 Prozent der bisherigen Deckung je Wolke.
 */
const KONTUR_WOLKE = 0.4;

/**
 * Wo eine Wolke ohne Drift steht. `translate(x) scale(s)` verschiebt ERST
 * und skaliert DANN — die Stellung ist also `x + s * pfad_x`.
 *
 * ⚠️ Genau diese Reihenfolge wurde beim Setzen der Zahlen zweimal falsch
 *    angenommen. Deshalb rechnet sie jetzt der Code, und der Test prueft das
 *    Ergebnis, statt dass beide dieselbe Schaetzung wiederholen.
 */
export function wolkenGrundstellung(i: number): { von: number; bis: number } {
  const w = WOLKEN_LAGEN[i];
  const m = w.d === "nah" ? WOLKE_MASSE.nah : WOLKE_MASSE.fern;
  return { von: w.x + w.s * m.x, bis: w.x + w.s * (m.x + m.breite) };
}

function wolkenBild(zahl: number): string {
  if (zahl <= 0) return "";
  /*
   * ⚠️ Der x-Versatz ist wichtiger als er aussieht. (05.09., zweiter Anlauf)
   *
   * Zuerst standen hier nur `y` und `scale` — die vier Wolken nutzten also
   * zwei Formen an zwei Stellen und lagen paarweise ÜBEREINANDER. Auf
   * Martins Bild sah ein zu 100 Prozent bedeckter Himmel dadurch aus wie
   * eine einzelne Wolke am Rand.
   *
   * ⚠️ 09.09.2026 — hier stand dazu „die Formen sind um x≈50 (nah) und
   *    x≈156 (fern) gezeichnet". Das war geschaetzt und falsch; die
   *    gemessenen Werte stehen bei WOLKE_MASSE, und die Grundstellungen
   *    rechnet wolkenGrundstellung(). Eine zweite, abweichende Erklaerung in
   *    derselben Datei hat den Fehler ueberhaupt erst ueberleben lassen.
   */

  /*
   * ⚠️ ZWEI Ebenen, wie beim Vogelzug — und aus demselben Grund.
   *
   * Im ersten Anlauf stand der Stellungs-Versatz als `transform` AM PFAD,
   * und die CSS-Animation setzt ebenfalls `transform`. Sie gewinnt: Im
   * Browser gemessen standen Wolke 1 bei x=55..146 und Wolke 4 bei
   * x=58..149 — übereinander, obwohl 250 Einheiten Versatz im Code standen.
   *
   * Die ÄUSSERE Gruppe trägt jetzt die Stellung, das innere Element den
   * Drift. Zwei Dinge, die dasselbe Attribut wollen, brauchen zwei Ebenen.
   */
  return WOLKEN_LAGEN.slice(0, Math.min(zahl, WOLKEN_LAGEN.length))
    .map(
      (w) =>
        `<g transform="translate(${w.x},${w.y}) scale(${w.s})">
          <!-- ⚠️ 10.09.2026 — fill-opacity war 0.6, die Wolken waren glaesern.
               Martin, mit Bild: „Die Wolken sollten sich auch nicht transparent
               zeigen … sieht man in der Regel nicht, wenn sie sich
               überschneiden." Jetzt deckt die vordere die hintere. Der
               Fuellgrund ist ein Verlauf aus deckenden Farben — die Wolke
               bleibt heller als der Himmel, nur nicht mehr durchsichtig. -->
          <path class="szene-wolke${w.klasse}" d="${w.d === "nah" ? WOLKE_NAH : WOLKE_FERN}"
            fill="url(#szene-wolkengrund)" fill-opacity="1"
            stroke="var(--szene-strich)" stroke-width="${w.breite}"
            stroke-opacity="${abgerundet(w.deckung * KONTUR_WOLKE)}"/>
        </g>`,
    )
    .join("\n    ");
}

/**
 * Baut die Szene als SVG-Markup.
 *
 * Feste Breite 390 im viewBox, aber `width="100%"` — sie skaliert mit dem
 * Blatt, statt auf einer Breite zu klemmen.
 */
export function tagesszene(opts: {
  lage?: Wetterlage;
  zeit?: Tageszeit;
  /**
   * Kompakt fuer die Wetter-Kachel: ein eigenes, engeres Bild statt eines
   * Ausschnitts aus der breiten Szene.
   *
   * ⚠️ Der erste Anlauf zeigte die BREITE Szene in einem 92px-Fenster — im
   * Browser war davon nur ein Stueck Wolkenrand zu sehen, das wie ein Fleck
   * aussah. Ein Ausschnitt ist kein Bild.
   */
  kompakt?: boolean;
  /** Jetzt — bestimmt Stand auf der Bahn und Mondphase. */
  jetzt?: Date;
  /** Echte Sonnenzeiten aus dem Wetter. Fehlen sie, gelten feste Stunden. */
  sonne?: Partial<Sonnenzeiten>;
  /** Bewölkung in Prozent (0–100), aus /api/weather/now. */
  bewoelkung?: number | null;
  /**
   * Niederschlag des Tages in Millimetern. Bestimmt, WIE STARK es regnet —
   * der Wettercode sagt „Regen", aber nicht wie viel. (11.09.2026)
   */
  regenMm?: number | null;
  /** 0–1; entscheidet über die Überraschung. Fehlt er, würfelt die Szene. */
  wuerfel?: number;
  /**
   * Wie viel breiter als hoch der Boden gezogen wird — siehe
   * {@link bodenStreckung}. 1 = gleichmäßig (Desktop-Karte), am Telefon mit
   * dem flachen Band ~1,74. Fehlt er, gilt 1.
   */
  bodenStreckung?: number;
  /**
   * Welche Jahreszeit gezeichnet wird (07.10.2026). Fehlt sie, gilt der
   * Kalender (meteorologisch, Wiener Datum) — siehe shared/jahreszeit.ts.
   * Die Einstellung „Jahreszeit der Szene" reicht zuhause-view hier herein.
   */
  jahreszeit?: Jahreszeit;
}): string {
  const lage = opts.lage ?? "unbekannt";
  const zeit = opts.zeit ?? "morgen";
  const jetzt = opts.jetzt ?? new Date();
  const jz = opts.jahreszeit ?? jahreszeitAmDatum(jetzt);
  if (opts.kompakt) return kompakteSzene(lage, zeit, jz);
  /*
   * ⚠️ ZWEI Ebenen, seit 29.08. abends. Martin, am Rechner: *„Die passt ja
   * überhaupt nicht mehr — die Darstellung von der Fußleiste und auch der
   * Header von Zuhause."*
   *
   * Gemessen bei 1440 Punkten Schirmbreite: das Band ist 1112 breit und 160
   * hoch. Das Bild ist fuer 390x160 gezeichnet, also 2,44:1; das Band ist
   * 6,95:1. Mit `slice` skaliert der Browser auf Deckung — 2,85-fach — und
   * schneidet oben ab. Uebrig blieb ein Streifen Huegel: kein Himmel, keine
   * Sonne, kein Mond.
   *
   * Ein einzelnes Bild kann das nicht loesen: Deckung schneidet, Einpassen
   * laesst Luecken, und `none` zerrt die Sonne zur Ellipse.
   *
   * Also getrennt nach dem, was Zerren vertraegt:
   *   BODEN   Huegel, Baeume, Graeser — `none`, volle Breite. Eine weiche
   *           Kurve darf breiter werden, das sieht niemand.
   *   HIMMEL  Sonne, Mond, Wolke, Regen, Voegel, Sterne — Seitenverhaeltnis
   *           gewahrt, rechts gehalten. Ein gezerrter Kreis faellt sofort auf.
   *
   * ⚠️ Baeume und Huette gehoeren zum BODEN: sie stehen auf den Huegeln und
   * muessen mit ihnen wandern. Gezogen werden sie aber nicht — jede Gruppe
   * traegt `scale(1/Streckung 1)` und bleibt damit rund (siehe baum).
   */
  /*
   * Der Versatz der Zugbewegungen. (05.09.2026)
   *
   * ⚠️ Die Szene wird bei jedem Stundenwechsel NEU GEBAUT (`innerHTML` in
   *    zuhause-view). Ohne diesen Wert begänne jede CSS-Animation dann wieder
   *    bei null — die Wolke spränge einmal pro Stunde an den linken Rand
   *    zurück, sichtbar und hässlich.
   *
   * Mit den Sekunden seit Mitternacht als negativem `animation-delay` steht
   * jede Bewegung dort, wo sie zu dieser Tageszeit stehen muss. Ein Neuaufbau
   * ist damit unsichtbar.
   */
  const seitMitternacht =
    jetzt.getHours() * 3600 + jetzt.getMinutes() * 60 + jetzt.getSeconds();
  const extra = ueberraschung(zeit, opts.wuerfel ?? Math.random(), lage);
  // Das Blatt faellt am Tag und nicht im Regen — Wetter bleibt massgeblich.
  const fallendesBlatt = lage !== "regen" && (zeit === "morgen" || zeit === "mittag");
  // Die Jahreszeit entscheidet mit: kein Schmetterling im Winter und nicht
  // mehr nach Oktober (siehe falterImJahr). Die Überraschung selbst würfelt
  // weiter wie bisher.
  const falter = falterImJahr(jz, jetzt) ? extra.boden : "";
  // 10.10.2026: Morgenröte — Morgenhimmel ohne Sonne, Licht im Haus noch an.
  const daemmert = morgenroete(jetzt, zeit, opts.sonne);
  return `<div class="zuhause-szene" data-zeit="${zeit}" data-jahreszeit="${jz}"${daemmert ? ' data-daemmerung="morgen"' : ""} style="--zug-alter: -${seitMitternacht}s">
    <svg class="szene-himmel" viewBox="0 0 390 160" width="100%" height="160"
      fill="none" aria-hidden="true" preserveAspectRatio="xMaxYMin meet">
      ${gestirn(zeit, lage, jetzt, opts.sonne, opts.bewoelkung)}
      ${wolke(lage, opts.bewoelkung)}
      ${regen(lage, opts.regenMm)}
      ${extra.himmel}
      ${voegel(zeit, jetzt, bahnPunkt(bahnFortschritt(jetzt, opts.sonne, zeit === "nacht")).x)}
    </svg>
    <svg class="szene-boden" viewBox="0 0 390 160" width="100%" height="160"
      fill="none" aria-hidden="true" preserveAspectRatio="none">
      ${/*
        07.10.2026 (Entwurf „ruhiger"): die Reihenfolge ist die Tiefe. Der
        ferne und der mittlere Hügel, darauf die Pappel und das Haus, DANN
        der nahe Hügel — er verdeckt den Fuß des Weges. Vorn die zwei großen
        Bäume. Die Höhenlinien (graeser) sind weg: weniger Strich, mehr Ruhe.
      */ ""}
      ${huegel()}
      ${hintererBaum(opts.bodenStreckung, zeit, jz)}
      ${huette(zeit, lage, jetzt, opts.bodenStreckung, jz, daemmert)}
      ${nahHuegel()}
      ${wiese(jz, zeit, opts.bodenStreckung)}
      ${baeume(opts.bodenStreckung, zeit, fallendesBlatt, jz)}
      ${zeit === "nacht" ? gluehwuermchen(opts.bodenStreckung, jz) : ""}
      ${falter}
      ${TIEFE}
      ${schneefall(jz, lage, opts.bodenStreckung)}
    </svg>
  </div>`;
}

/**
 * Zwei Voegel, klein und weit oben.
 *
 * ⚠️ Sie sitzen zwischen Wolke und Sonne, nicht darauf. Ein Vogel VOR der
 * Sonne sieht aus wie ein Kratzer im Kreis.
 */
/* ═══════════════════════════════════════════════════════════════════
   DAS LEBEN AM HIMMEL — 2026-09-04

   Martin: *„Die Vögel könnten vielleicht einmal da sein und einmal weg und
   vielleicht einmal etwas anderes, sodass das Bild noch lebendiger wird."*

   Vorher standen dieselben drei Vögel an denselben drei Stellen — jeden
   Tag, jede Stunde.

   ⚠️ NICHT per Zufall. Die Regel dieser Datei gilt weiter: dieselbe Seite
   soll bei jedem Aufbau gleich aussehen. Springende Vögel bei jedem
   Neuzeichnen wären Unruhe, kein Himmel. Also entscheidet der TAG — heute
   ein Schwarm, morgen ein einzelner, übermorgen leerer Himmel. Lebendig
   über die Tage, ruhig innerhalb eines Tages.
   ═══════════════════════════════════════════════════════════════════ */

/** Ein stabiler kleiner Wert aus dem Datum. Gleicher Tag, gleiches Bild. */
function tagesZahl(jetzt: Date): number {
  const t = jetzt.getFullYear() * 10000 +
    (jetzt.getMonth() + 1) * 100 + jetzt.getDate();
  // Ein bisschen mischen, damit aufeinanderfolgende Tage nicht durchzaehlen.
  return (t * 2654435761) % 997;
}

function voegel(zeit: Tageszeit, jetzt: Date, sonneX: number): string {
  // ⚠️ Nachts fliegt hier nichts. Drei Voegel im Sternenhimmel sahen aus wie
  // Kratzer — und nachts fliegen keine.
  if (zeit === "nacht") return "";
  /*
   * ⚠️ Seit die Sonne ueber das ganze Bild wandert, kreuzt sie jede feste
   * Vogelstelle irgendwann. Im ersten Anlauf flogen sie um 13 Uhr MITTEN
   * DURCH die Scheibe — im Bild sofort zu sehen.
   *
   * Die Voegel weichen deshalb aus: Steht die Sonne links, fliegen sie
   * rechts, und umgekehrt. Sie sind um x≈200 gezeichnet; die Verschiebung
   * setzt sie in die freie Haelfte.
   */
  const dx = sonneX < 195 ? 110 : -110;
  /*
   * ⚠️ ZWEI Gruppen, nicht eine. (05.09.2026)
   *
   * Martin: *„Es gibt noch einen kleinen Spalt zwischen ‚Wolken bewegen
   * sich' und ‚die Sonne bewegt sich', aber die Vögel bleiben ruhig. Was
   * geht ja auch irgendwie nicht, weil die Vögel sich natürlich sicher
   * bewegen."*
   *
   * Er hat recht, und zwar strenger als bei den Wolken: Eine stehende Wolke
   * ist denkbar, ein stehender Vogel nicht.
   *
   * Die INNERE Gruppe trägt weiter das Ausweichen vor der Sonne (dx), die
   * ÄUSSERE den Flug. Beides in ein `transform` zu legen ginge nicht — die
   * CSS-Animation würde das Attribut überschreiben und die Vögel wieder in
   * die Sonne setzen.
   */
  const g = (d: string, deckung = 0.3): string =>
    `<g class="szene-vogelzug">
    <g transform="translate(${dx},0)" stroke="var(--ink)" stroke-width="1.5" stroke-linecap="round" opacity="${deckung}">
      <path d="${d}"/>
    </g>
  </g>`;
  switch (tagesZahl(jetzt) % 5) {
    case 0:
      return ""; // leerer Himmel — auch das kommt vor
    case 1:
      return g("M204 40c5-6 10-6 14 0", 0.28); // ein einzelner, hoch
    case 2:
      // ein Schwarm, weiter weg und blasser
      return g(
        "M150 34c4-5 8-5 11 0M170 28c3-4 7-4 10 0M190 38c3-4 7-4 10 0" +
          "M208 30c3-4 6-4 9 0M226 42c3-4 6-4 9 0",
        0.22,
      );
    case 3:
      return g("M186 44c5-6 10-6 14 0M212 34c4-5 8-5 12 0", 0.3); // zwei
    default:
      // die vertrauten drei
      return g("M178 46c5-6 10-6 14 0M200 36c4-5 8-5 12 0M222 50c3-4 7-4 10 0");
  }
}

/**
 * DREI Huegelebenen statt zwei.
 *
 * 2026-08-29, zweite Runde (Martin: „vielleicht kann man die Grafik oben noch
 * etwas schöner machen"): Mit zwei Ebenen und zwei einzelnen Baeumen sah die
 * Szene leer aus — Einzelteile in viel Himmel, keine Landschaft. Tiefe
 * entsteht durch gestaffelte Ebenen: hell und hoch hinten, dunkel und tief
 * vorn. Das ist der ganze Trick.
 */
function huegel(): string {
  /*
   * 2026-09-03 (Martin: "das Erscheinungsbild noch schoener machen").
   *
   * Vorher trugen alle drei Ketten DIESELBE Farbe, nur mit anderer
   * Deckkraft — das ergibt Grauschleier, keine Tiefe. Ein Berg in der Ferne
   * ist nicht blasser, er ist KUEHLER und naeher am Himmel; die Luft
   * dazwischen faerbt ihn. Das nennt sich Luftperspektive und ist der
   * Unterschied zwischen einer Schablone und einer Landschaft.
   *
   * Jetzt drei eigene Toene, die aus dem Himmel herauswachsen (fern) und zur
   * Tinte hin sinken (nah). Definiert in style.css je Tageszeit, damit
   * Morgen, Mittag und Nacht ihre eigene Luft haben.
   *
   * ⚠️ Keine Deckkraft mehr. Sie war der Grund, warum die Ketten sich
   * gegenseitig durchscheinen liessen und dabei matschten.
   *
   * 07.10.2026 (Entwurf „ruhiger"): die drei Linien des Entwurfs — flacher
   * und weicher, das Haus steht auf dem mittleren Hügel. Der ferne trifft
   * den Horizont der Bahn (HORIZONT = 104) an beiden Enden weiter: links
   * bei x = 46 liegt er auf ≈ 101, rechts bei x = 344 auf ≈ 104.
   * Der NAHE Hügel steht in nahHuegel(), weil Pappel und Haus zwischen ihm
   * und dem mittleren stehen.
   */
  return `<path d="M0 108C50 98 110 104 170 107S300 100 390 106V160H0Z"
      fill="var(--szene-huegel-fern)"/>
    <path d="M0 119C70 109 140 118 215 115S340 109 390 115V160H0Z"
      fill="var(--szene-huegel-mitte)"/>`;
}

/**
 * Die Tiefe unten (Entwurf „ruhiger"): ein zarter Schatten über dem
 * Vordergrund, von nichts bei der Bildmitte bis 20 Prozent am Rand —
 * der Boden liegt dadurch vor dem Himmel, statt auf ihm zu kleben.
 * Ein Knoten, keine Bewegung.
 */
const TIEFE = `<defs>
        <linearGradient id="szene-tiefe" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="var(--szene-baumschatten)" stop-opacity="0"/>
          <stop offset="1" stop-color="var(--szene-baumschatten)" stop-opacity="0.2"/>
        </linearGradient>
      </defs>
      <rect class="szene-tiefe" x="0" y="80" width="390" height="80" fill="url(#szene-tiefe)"/>`;

/** Der nahe Hügel — vor Haus und Pappel, hinter den zwei großen Bäumen. */
function nahHuegel(): string {
  return `<path d="M0 133C80 123 150 135 230 131S350 124 390 130V160H0Z"
      fill="var(--szene-huegel-nah)"/>`;
}

/**
 * Drei klare Bäume. (07.10.2026, Entwurf „ruhiger")
 *
 * Martin zum alten Bild: *„Die Häuser sind so eigenartig violett, und die
 * Bäume verstehe ich nicht ganz. Bitte die Grafik noch schöner machen …
 * ganz ruhig."* Zum Entwurf: *„Wow, sieht ganz toll aus."*
 *
 * Vorher Eiche, Birke und Blütenbaum mit Lichtkanten, Blattpunkten und
 * Streifen — elf Einzelheiten je Baum, die auf 390 Punkten Breite zu
 * Rauschen wurden. Jetzt drei Formen, die man auf einen Blick liest:
 *
 *   links   runde Krone, groß, vorn am Hang
 *   Mitte   schlanke Pappel, klein, hinten auf dem mittleren Hügel
 *   rechts  runde Krone, vorn rechts
 *
 * Jede Krone hat EINEN Grundton und EINE Lichtseite (`--szene-<ton>-1/-2`).
 * Das Licht kommt von links oben, abends von der Sonne rechts.
 *
 * ⚠️ Drei Lehren gelten weiter:
 *   1. Ungleiche Größen, die Reihenfolge im Markup ist die Tiefe.
 *   2. Der Boden hat `preserveAspectRatio="none"`. Ein Baum steht in einer
 *      Gruppe, die waagrecht um genau die Streckung SCHMALER gemacht wird
 *      (`scale(1/f 1)`) — siehe bodenStreckung.
 *   3. Die Gruppe des Baums hat KEINE Untergruppe (die Tests schneiden sie
 *      am ersten `</g>`). Das Wiegen sitzt deshalb AUSSEN in `szene-wiegen`:
 *      eine CSS-Animation auf `transform` ersetzte sonst das
 *      Transform-Attribut des Baums, und er spränge an den Bildrand.
 * ⚠️ Keine Farben im Markup — nur Token (style.css, „Licht × Stoff").
 *
 * JE JAHRESZEIT (07.10.2026, Martin: „auch noch den Herbst, den Winter und
 * den Frühling"): Die Formen bleiben, nur zwei Stellen wechseln die Art —
 *   Frühling  rechts ein BLÜTENBAUM (rosa-weiße Krone, wenige große Blüten)
 *   Winter    links ein KAHLER Laubbaum mit Schnee auf den Ästen, in der
 *             Mitte und rechts FICHTEN mit Schnee auf jeder Stufe.
 * Die Farben kommen aus den Jahreszeit-Tokens (`--jz-*`, style.css).
 * ⚠️ Fichten nur im Winter: Martin wollte am 01.10. „normale Bäume statt
 *    Nadelbäumen". Im Winter trägt ein Laubbaum kein Laub — die Fichte ist
 *    dort das, was grün bleibt.
 */
type Baumart = "rund" | "pappel" | "bluete" | "kahl" | "fichte";
/** Welche Tokenfamilie die Krone trägt: `--szene-laub-*`, `-pappel-*`, `-krone-*`. */
type Baumton = "laub" | "pappel" | "krone";

const abgerundet = (n: number): number => Math.round(n * 100) / 100;

/** Wo die drei Bäume stehen (Entwurf). Massstab 1 ≈ 37 Einheiten Höhe. */
const BAUM_STAND = {
  links: { x: 62, fuss: 136, massstab: 1.05 },
  mitte: { x: 301, fuss: 118, massstab: 0.85 },
  rechts: { x: 341, fuss: 139, massstab: 0.92 },
} as const;

// Stamm der runden Krone und der Pappel — leicht gekehlt, unten breiter.
const STAMM_RUND = "M-2.1 1.5C-1.8-4-1.6-9-1.4-14H1.4C1.6-9 1.9-4 2.2 1.5Z";
const STAMM_PAPPEL = "M-1.4 1.2C-1.2-5-1-10-0.9-16H0.9C1-10 1.2-5 1.4 1.2Z";
// Der kahle Baum: kräftiger Stamm, wenige klare Äste, dünne Zweige.
const STAMM_KAHL = "M-2.4 1.5C-2-4-1.8-9-1.5-16H1.5C1.8-9 2.1-4 2.5 1.5Z";
const AESTE =
  "M0-15L-1.2-21.5L-7-28.5M-1.2-21.5L-0.4-33.5M0-15L1.4-20.5L7.5-26.5M1.4-20.5L3.6-31M-1.2-21.5L-9.5-22.5M1.4-20.5L10-19.5";
const ZWEIGE =
  "M-7-28.5L-9-32M-7-28.5L-5.2-32.6M-0.4-33.5L-2-37M-0.4-33.5L1.4-37.2M7.5-26.5L9.6-29.6M7.5-26.5L6-30.6M3.6-31L4.8-34.8M-9.5-22.5L-12-24.6M10-19.5L12.6-21.2";
// Schneekappen auf Astgabeln und -enden (x, y, r).
const SCHNEE_AESTE: ReadonlyArray<readonly [number, number, number]> = [
  [-7, -29.6, 1.5], [-0.4, -34.6, 1.4], [7.5, -27.6, 1.5], [3.6, -32.1, 1.2],
  [-9.5, -23.5, 1.3], [10, -20.5, 1.3], [-1.2, -22.6, 1.3], [1.4, -21.6, 1.2],
];
// Fichte: drei Stufen (oben halbe Breite a an y0, unten b an y1).
const FICHTE_STUFEN: ReadonlyArray<readonly [number, number, number, number]> = [
  [0, 6.5, -40, -28],
  [3.5, 9, -28, -18],
  [5, 11.5, -18, -6],
];
const FICHTE = "M0-40L-6.5-28H-3.5L-9-18H-5L-11.5-6H11.5L5-18H9L3.5-28H6.5Z";
const STAMM_FICHTE = "M-1.5 1.5V-6.5H1.5V1.5Z";
/** Die Lichtseite der Fichte: die halbe Form, auf der Seite l (−1 links, +1 rechts). */
const fichteLicht = (l: number): string =>
  `M0-40L${6.5 * l}-28H${3.5 * l}L${9 * l}-18H${5 * l}L${11.5 * l}-6H0Z`;
/** Schnee auf jeder Stufe: ein Kragen oben, unten weich gewellt. */
function fichtenSchnee(): string {
  return FICHTE_STUFEN.map(([a, b, y0, y1]) => {
    const xa = abgerundet(a + (b - a) * 0.42);
    const ya = abgerundet(y0 + (y1 - y0) * 0.42);
    return `M${-a} ${y0}L${-xa} ${ya}Q${abgerundet(-xa / 2)} ${abgerundet(ya - 1.6)} 0 ${abgerundet(ya - 0.5)}Q${abgerundet(xa / 2)} ${abgerundet(ya - 1.6)} ${xa} ${ya}L${a} ${y0}Z`;
  }).join("");
}
// Blütenbaum: wenige große, weiche Blüten — rosa und weiß (x, y).
const BLUETEN_ROSA: ReadonlyArray<readonly [number, number]> = [[-5, -31], [5, -29], [10, -21], [-11, -19], [-1, -20], [4, -16], [-6, -24]];
const BLUETEN_WEISS: ReadonlyArray<readonly [number, number]> = [[1, -33], [-2, -26], [8, -25], [-8, -15]];

function baum(
  x: number,
  fuss: number,
  massstab: number,
  streckung = 1,
  art: Baumart = "rund",
  ton: Baumton = "laub",
  zeit: Tageszeit = "mittag",
  wiegen = 1,
): string {
  const f = streckung > 0 ? streckung : 1;
  const sx = Math.round((massstab / f) * 1000) / 1000;
  const l = zeit === "abend" ? 1 : -1; // Lichtseite
  /*
   * 07.10.2026 (Prüfbefund): nachts war der Blütenbaum ein grauer Ball —
   * die helle rosa Krone, mit dem Nachtblau gemischt, wurde das Hellste nach
   * dem Mond. Nachts trägt er darum ein eigenes dunkles Rosa, ohne Lichtseite.
   */
  const bluetenNacht = art === "bluete" && zeit === "nacht";
  const grund = bluetenNacht ? "var(--szene-bluete-nacht)" : `var(--szene-${ton}-1)`;
  const licht = bluetenNacht ? "var(--szene-bluete-nacht)" : `var(--szene-${ton}-2)`;
  const schatten = (rx: number, ry: number, cy: number): string =>
    `<ellipse class="szene-baumschatten" cx="0" cy="${cy}" rx="${rx}" ry="${ry}" fill="var(--szene-baumschatten)" fill-opacity="0.28"/>`;
  const kreis = (cx: number, cy: number, r: number, fill: string, deckung?: number): string =>
    `<circle cx="${abgerundet(cx)}" cy="${cy}" r="${r}" fill="${fill}"${deckung ? ` fill-opacity="${deckung}"` : ""}/>`;
  const rund = `${schatten(14, 2.4, 1.4)}
    <path d="${STAMM_RUND}" fill="var(--szene-stamm)"/>
    ${kreis(0, -24, 12, grund)}${kreis(-9, -17.5, 8, grund)}${kreis(9.5, -18, 8.5, grund)}
    ${kreis(l * 3.5, -28, 7.5, licht, 0.7)}${kreis(l * 9.5, -20.5, 4.5, licht, 0.5)}`;
  let innen: string;
  switch (art) {
    case "pappel":
      innen = `${schatten(8, 2, 1.2)}
    <path d="${STAMM_PAPPEL}" fill="var(--szene-stamm)"/>
    <ellipse cx="0" cy="-27" rx="7.2" ry="16.5" fill="${grund}"/>
    <ellipse cx="${abgerundet(l * 2.2)}" cy="-30.5" rx="3.4" ry="10" fill="${licht}" fill-opacity="0.55"/>`;
      break;
    case "bluete":
      // Nachts schließen die Blüten — die Krone bleibt, rosa gedämpft.
      innen =
        rund +
        (zeit === "nacht"
          ? ""
          : `
    <path d="${punkte(BLUETEN_ROSA, 1.8)}" fill="var(--szene-bluete)"/><path d="${punkte(BLUETEN_WEISS, 1.4)}" fill="var(--szene-bluete-hell)"/>`);
      break;
    case "kahl":
      innen = `${schatten(12, 2.2, 1.4)}
    <path d="${STAMM_KAHL}" fill="var(--szene-stamm)"/>
    <path d="${AESTE}" fill="none" stroke="var(--szene-stamm)" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="${ZWEIGE}" fill="none" stroke="var(--szene-stamm)" stroke-width="0.9" stroke-linecap="round"/>
    <path d="${kreisPfad(SCHNEE_AESTE)}" fill="var(--szene-schnee)"/>`;
      break;
    case "fichte":
      innen = `${schatten(12, 2.2, 1.4)}
    <path d="${STAMM_FICHTE}" fill="var(--szene-stamm)"/>
    <path d="${FICHTE}" fill="${grund}"/>
    <path d="${fichteLicht(l)}" fill="${licht}" fill-opacity="0.45"/>
    <path d="${fichtenSchnee()}" fill="var(--szene-schnee)"/>`;
      break;
    default:
      innen = rund;
  }
  return `<g class="szene-wiegen szene-wiegen--${wiegen}"><g class="szene-baum" transform="translate(${x} ${fuss}) scale(${sx} ${massstab})">
    ${innen}</g></g>`;
}

/** Viele kleine Punkte in EINEM Pfad (ein Knoten statt zehn Kreise). */
function punkte(liste: ReadonlyArray<readonly [number, number]>, r: number): string {
  return kreisPfad(liste.map(([x, y]) => [x, y, r] as const));
}

/** Die Pappel hinten auf dem mittleren Hügel (im Winter eine Fichte) — vor dem Haus gezeichnet. */
function hintererBaum(streckung = 1, zeit: Tageszeit = "mittag", jz: Jahreszeit = "sommer"): string {
  const s = BAUM_STAND.mitte;
  return baum(s.x, s.fuss, s.massstab, streckung, jz === "winter" ? "fichte" : "pappel", "pappel", zeit, 2);
}

/** Die Maße der Zeichnung (viewBox) — dieselben wie im Markup der Szene. */
const SZENE_BREITE = 390;
const SZENE_HOEHE = 160;

/**
 * Wie stark der Boden waagrecht MEHR gezogen wird als senkrecht.
 * (23.09.2026 — Martin, mit Bild: „Das Bild oben ist jetzt verzerrt, warum?")
 *
 * ⚠️ Der Boden hat `preserveAspectRatio="none"`: er füllt jede Fläche. Das
 *    war gewollt — eine weiche Hügelkurve darf breiter werden. Seit pre.3183
 *    (Zuhause neu geordnet) ist das Band am Telefon aber nur 92 statt 160
 *    hoch. Waagrecht bleibt der Maßstab 1, senkrecht 0,575: jeder Baum wurde
 *    fast doppelt so breit wie hoch, der Himmel (Sonne, Wolken — `meet`)
 *    blieb rund. Das war die Verzerrung.
 *
 *    Die Hügel dürfen weiter gezogen werden. Die Bäume nicht: sie werden um
 *    genau diesen Faktor schmaler gezeichnet und stehen danach im selben
 *    gleichmäßigen Maßstab wie Sonne und Wolken.
 *
 * @param breite       tatsächliche Breite des Bandes in Punkten
 * @param zeichnung    tatsächliche Höhe der Zeichnung in Punkten
 */
export function bodenStreckung(breite: number, zeichnung: number): number {
  if (!(breite > 0) || !(zeichnung > 0)) return 1;
  const f = breite / SZENE_BREITE / (zeichnung / SZENE_HOEHE);
  // Auf zwei Stellen: der Wert steht im Schlüssel des Neuaufbaus — ein
  // halber Punkt Rundung darf keinen neuen Anstrich auslösen.
  return Math.round(f * 100) / 100;
}


function baeume(
  streckung = 1,
  zeit: Tageszeit = "mittag",
  blatt = false,
  jz: Jahreszeit = "sommer",
): string {
  /*
   * Die zwei großen Bäume vorn. (07.10.2026, Entwurf „ruhiger")
   *
   * Seit 02.10. stehen nur noch drei Bäume (Martin: „vielleicht ein bisschen
   * weniger Bäume"); die Pappel hinten zeichnet hintererBaum() VOR dem Haus.
   * Die Reihenfolge ist die Tiefe (kleinster Fuß-y zuerst).
   */
  const l = BAUM_STAND.links;
  const r = BAUM_STAND.rechts;
  const winter = jz === "winter";
  const gruppe = [
    baum(l.x, l.fuss, l.massstab, streckung, winter ? "kahl" : "rund", "laub", zeit, 1),
    baum(r.x, r.fuss, r.massstab, streckung, winter ? "fichte" : jz === "fruehling" ? "bluete" : "rund", "krone", zeit, 3),
  ].join("");
  return gruppe + (blatt ? fallblatt(streckung, jz) : "");
}

/**
 * Ein einzelnes Blatt loest sich von der Krone und trudelt hinunter —
 * selten, still, nur am Tag und nicht im Regen. (01.10.2026, Ticket 2892)
 *
 * ⚠️ Eine Ueberraschung, kein Schmuck: In 120 Sekunden Umlauf ist es etwa
 *    zwanzig Sekunden zu sehen. Die Bewegung steht im Blatt (`szene-blatt`).
 *
 * 07.10.2026, je Jahreszeit: im HERBST fallen drei Blätter (eines je Baum,
 * jedes im eigenen, kürzeren Takt — höchstens drei zugleich), im FRÜHLING
 * ein Blütenblatt vom Blütenbaum, im SOMMER das eine Blatt wie bisher, im
 * WINTER keines (dort fällt Schnee).
 */
const BLATT = "M0 0C2-3 6-3 8 0C6 3 2 3 0 0Z";
const BLUETENBLATT = "M0 0C1.4-2.6 4.6-2.6 5.4 0C4.6 2.6 1.4 2.6 0 0Z";
function fallblatt(streckung = 1, jz: Jahreszeit = "sommer"): string {
  if (jz === "winter") return "";
  const f = streckung > 0 ? streckung : 1;
  const sx = Math.round((1 / f) * 1000) / 1000;
  const blatt = (x: number, y: number, klasse = ""): string =>
    `<g transform="translate(${x} ${y}) scale(${sx} 1)">
    <path class="szene-blatt${klasse}" d="${jz === "fruehling" ? BLUETENBLATT : BLATT}" fill="var(--szene-blatt)"/>
  </g>`;
  if (jz === "herbst") return blatt(52, 104) + blatt(296, 96, " szene-blatt--zwei") + blatt(344, 112, " szene-blatt--drei");
  if (jz === "fruehling") return blatt(336, 110);
  return blatt(52, 104);
}

/**
 * Wiesentupfen auf dem nahen Hügel — nur im Frühling (bunt) und im Sommer
 * (gelb), nur bei Tag: nachts schließen die Blumen, wie am Blütenbaum.
 * Zwei Pfade, zwei Töne (`--szene-tupfen-1/-2`).
 */
const TUPFEN_EINS: ReadonlyArray<readonly [number, number, number]> = [
  [22, 143, 1.3], [104, 139, 1.2], [160, 146, 1.3], [246, 138, 1.2], [288, 147, 1.3], [378, 141, 1.2],
];
const TUPFEN_ZWEI: ReadonlyArray<readonly [number, number, number]> = [
  [40, 149, 1.1], [124, 146, 1.1], [174, 140, 1], [262, 145, 1.1], [312, 141, 1], [364, 149, 1.1],
];
function wiese(jz: Jahreszeit, zeit: Tageszeit, streckung = 1): string {
  if (zeit === "nacht" || (jz !== "fruehling" && jz !== "sommer")) return "";
  const sx = 1 / (streckung > 0 ? streckung : 1);
  return `<path class="szene-tupfen" d="${kreisPfad(TUPFEN_EINS, sx)}" fill="var(--szene-tupfen-1)"/>
      <path class="szene-tupfen" d="${kreisPfad(TUPFEN_ZWEI, sx)}" fill="var(--szene-tupfen-2)"/>`;
}

/**
 * Ruhiger Schneefall — im Winter, solange nicht die Sonne scheint.
 * (07.10.2026)
 *
 * ⚠️ Wetter bleibt maßgeblich (Martin, 05.09.: „Regenschauer und
 *    Sonnenschein ist ein Widerspruch"): bei klarer Sonne fällt kein Schnee,
 *    unter Wolken, bei Niederschlag und ohne Wetter ja. Nachts ebenso — die
 *    Sterne bleiben, wo der Himmel offen ist.
 * ⚠️ Zwei Ebenen (nah, fern), je EIN Pfad und EINE Bewegung: höchstens 13
 *    Flocken sind zugleich zu sehen, und nur zwei Elemente bewegen sich.
 *    Jede Ebene ist eine Kachel, die sich alle 160 Einheiten wiederholt —
 *    die Kopie steht genau eine Kachel höher und um den seitlichen Versatz
 *    der Bewegung verschoben. So ist der Neubeginn jeder Runde unsichtbar.
 * ⚠️ Nur Transformation (CSS `szene-schneefall`), mit dem Zug-Alter wie die
 *    Wolken, damit der stündliche Neuaufbau nicht springt.
 */
const FLOCKEN_NAH: ReadonlyArray<readonly [number, number, number]> = [
  [24, 18, 1.5], [78, 62, 1.4], [136, 30, 1.6], [188, 96, 1.3], [246, 48, 1.5], [300, 12, 1.4], [352, 78, 1.6],
];
const FLOCKEN_FERN: ReadonlyArray<readonly [number, number, number]> = [
  [50, 40, 0.9], [110, 110, 0.8], [168, 70, 1], [226, 8, 0.9], [284, 124, 0.8], [336, 44, 1],
];
/** Seitlicher Versatz je Runde (Einheiten) — muss zu den Keyframes im Blatt passen. */
export const SCHNEE_VERSATZ = { nah: 8, fern: 5 } as const;
function schneefall(jz: Jahreszeit, lage: Wetterlage, streckung = 1): string {
  if (jz !== "winter" || lage === "sonne") return "";
  const kachel = (liste: ReadonlyArray<readonly [number, number, number]>, dx: number): string =>
    kreisPfad([...liste, ...liste.map(([x, y, r]) => [x - dx, y - SZENE_HOEHE, r] as const)], 1 / (streckung > 0 ? streckung : 1));
  return `<g class="szene-schneefall"><path d="${kachel(FLOCKEN_NAH, SCHNEE_VERSATZ.nah)}" fill="var(--szene-flocke)"/></g>
      <g class="szene-schneefall szene-schneefall--fern"><path d="${kachel(FLOCKEN_FERN, SCHNEE_VERSATZ.fern)}" fill="var(--szene-flocke)" fill-opacity="0.75"/></g>`;
}

/**
 * Darf der Schmetterling kommen? Nicht im Winter, im Herbst nur bis Ende
 * Oktober (Wiener Datum) — danach ist es draußen zu kalt. (07.10.2026)
 */
export function falterImJahr(jz: Jahreszeit, jetzt: Date): boolean {
  if (jz === "winter") return false;
  if (jz === "herbst") {
    const m = wienerMonat(jetzt);
    return m === 9 || m === 10;
  }
  return true;
}

/** Schnee auf dem Dach (ein Kragen über beide Hänge) und auf dem Schornstein. */
const SCHNEE_DACH =
  "M-21.7-12.4L0-30.9L21.7-12.4L18.6-13L0-27.6L-18.6-13Z" +
  "M8.4-27.4V-28.6a1 1 0 0 1 1-1h4.2a1 1 0 0 1 1 1v1.2Z";

/* ═══════════════════════════════════════════════════════════════════
   DAS HAUS SCHLÄFT — 02.10.2026

   Martin: *„Es soll am Abend ab 24 Uhr aus sein, weil dann geschlafen
   wird. Das heißt, das Licht soll um 24 Uhr aus sein."*

   Die Regel, schlicht:
     23:50–24:00   das Licht verlischt (zehn Minuten, linear)
     00:00–04:50   aus — Fenster dunkel, Laterne aus, kein Rauch
     04:50–05:00   es wird wieder hell
   Brennen tut es nur, wenn es überhaupt brennen soll: abends, nachts und
   im Regen (siehe fensterLicht). Am hellen Tag bleibt das Fenster ohnehin
   dunkel, um 05:00 im Sommer ist es schon Morgen — dann bleibt es aus.

   ⚠️ WIENER Zeit (wienerMinute), nicht die Uhr des Geräts — dieselbe Regel
      wie bei der Tageszeit. Die Szene wird stündlich neu gebaut; dazwischen
      setzt rueckeGestirn jede Minute den Anteil (stelleHauslicht). Das Blatt
      macht aus den Minutenschritten einen weichen Übergang (`transition`
      auf opacity) — bei „Bewegung reduzieren" springt es sofort.
   ═══════════════════════════════════════════════════════════════════ */

/** Ab dieser Wiener Minute verlischt das Licht (23:50). */
const LICHT_AUS_AB = 23 * 60 + 50;
/** Ab dieser Wiener Minute wird es wieder hell (04:50); voll an um 05:00. */
const LICHT_AN_AB = 4 * 60 + 50;
/** So lange dauert jeder Übergang, in Minuten. */
const LICHT_UEBERGANG = 10;

/**
 * Wie wach das Haus ist: 1 = Licht kann brennen, 0 = es wird geschlafen.
 * Dazwischen nur in den beiden Übergängen (siehe oben).
 */
export function wachAnteil(jetzt: Date): number {
  const m = wienerMinute(jetzt);
  if (m >= LICHT_AUS_AB) return zweiStellen(1 - (m - LICHT_AUS_AB) / LICHT_UEBERGANG);
  if (m < LICHT_AN_AB) return 0;
  if (m < LICHT_AN_AB + LICHT_UEBERGANG) return zweiStellen((m - LICHT_AN_AB) / LICHT_UEBERGANG);
  return 1;
}

/** Soll überhaupt Licht brennen? Abends, nachts und im Regen — das Wetter spricht. */
export function lichtWetter(
  zeit: Tageszeit,
  lage: Wetterlage,
  jahreszeit?: Jahreszeit,
  daemmert = false,
): boolean {
  // 07.10.2026: im Winter brennt es auch tagsüber — ein warmes Fenster im
  // Schnee ist das Bild des Winters (Schlafenszeit gilt trotzdem).
  // 10.10.2026: in der Morgenröte brennt es noch — draußen ist es erst hell,
  // die Sonne ist nicht da.
  return zeit === "abend" || zeit === "nacht" || lage === "regen" || jahreszeit === "winter" || daemmert;
}

/**
 * Wie hell das Fenster jetzt ist, 0–1. Fasst beides zusammen: ob es brennen
 * SOLL (Tageszeit, Wetter) und ob jemand wach ist (wachAnteil).
 */
export function fensterLicht(
  jetzt: Date,
  zeit: Tageszeit,
  lage: Wetterlage,
  jahreszeit?: Jahreszeit,
  daemmert = false,
): number {
  return lichtWetter(zeit, lage, jahreszeit, daemmert) ? wachAnteil(jetzt) : 0;
}

/**
 * Setzt den Anteil im fertigen Bild — jede Minute aus rueckeGestirn.
 * Nur Attribute an den wenigen Licht-Gruppen; kein Neuaufbau.
 */
function stelleHauslicht(halter: ParentNode, jetzt: Date): void {
  const a = String(wachAnteil(jetzt));
  halter.querySelectorAll(".szene-licht, .szene-rauchfahne").forEach((el) => {
    if (el.getAttribute("opacity") !== a) el.setAttribute("opacity", a);
  });
}

/** Mehrere Kreise in EINEM Pfad — ein Knoten statt vieler (Busch, Blumen). */
/**
 * `sx` (07.10.2026, Prüfbefund „Szene nie pressen"): waagrechter Maßstab
 * der Bögen. Was frei im gestreckten Boden liegt (Wiesentupfen, Flocken),
 * steht in keiner Gruppe mit `scale(1/f 1)` — es wird mit `sx = 1/f`
 * schmaler gezeichnet und ist nach dem Ziehen wieder rund. Bei 820 Punkten
 * Breite (f ≈ 1,96) waren die Flocken sonst waagrechte Striche.
 */
function kreisPfad(liste: ReadonlyArray<readonly [number, number, number]>, sx = 1): string {
  return liste
    .map(([x, y, r]) => {
      const rx = abgerundet(r * sx);
      return `M${abgerundet(x - rx)} ${y}a${rx} ${r} 0 1 0 ${abgerundet(2 * rx)} 0a${rx} ${r} 0 1 0 ${abgerundet(-2 * rx)} 0`;
    })
    .join("");
}

/*
 * Das stille Detail am Haus. (08.10.2026)
 *
 * Martin, vor dem Schlafengehen: „Überrasche mich noch mit etwas Liebem,
 * vielleicht in dem Bild im Zuhause.“ Kein Fremdkörper und nichts Lautes:
 * EIN kleines Ding am Haus, das zur Szene gehört — und je nach Tag etwas
 * anderes ist, damit es nie dem Wetter widerspricht:
 *
 *   Regen          eine Wandlampe neben der Tür — ein warmes Licht, wo es nass ist.
 *   Abend, Nacht   ein Stern über dem Haus, einen Hauch größer und wärmer als
 *                  die anderen. (Abends ist es der Abendstern.)
 *   Morgen, Mittag zwei Spatzen auf dem Dach, einander zugewandt.
 *
 * ⚠️ Es steht im BODEN-Bild, in einer Gruppe neben dem Haus (`huette`) mit
 *    derselben Stelle und demselben Maßstab — nicht im Himmel: der Himmel
 *    wird nicht gezogen und sitzt rechts, das Haus steht auf dem gezogenen
 *    Boden; ein Stern im Himmel-Bild stünde je nach Breite NEBEN dem Haus.
 *    Es ist nicht IM Haus, weil die Haus-Gruppe ihre Teile zählt (Piktogramm).
 *    `scale(1/f 1)` gilt auch hier: rund bleibt rund.
 * ⚠️ Reine Dekoration (`aria-hidden`), keine Farbe im Markup — nur Token.
 *    Keine Schleife: jede Bewegung läuft einmal und bleibt dann stehen
 *    (Regel „Einmal durch, dann Ruhe"); bei Bewegungsverzicht steht das Bild
 *    von Anfang an fertig da.
 * ⚠️ Die Geometrie: der linke Dachhang geht von (-20.5 | -12.8) nach
 *    (0 | -29.5), die Füße sitzen 1,4 über dem Hang (halbe Strichstärke 1,1
 *    senkrecht gemessen = 1,4 lotrecht).
 */
function spatz(x: number, fussY: number, blickNachLinks: boolean, klasse: string): string {
  const spiegel = blickNachLinks ? " scale(-1 1)" : "";
  return `<g class="szene-spatz" transform="translate(${x} ${fussY})${spiegel}">
        <g class="szene-spatz-huepf${klasse}">
          <path d="M-1.5-2.3L-4-1.5L-3.7-0.7L-1.2-1.2Z" fill="var(--szene-stamm)"/>
          <path d="M-0.4-0.8V0.3M0.7-0.8V0.3" stroke="var(--szene-stamm)" stroke-width="0.4" stroke-linecap="round"/>
          <ellipse cx="0" cy="-2" rx="2" ry="1.55" fill="var(--szene-stamm)"/>
          <ellipse cx="0.5" cy="-1.5" rx="1.2" ry="0.75" fill="var(--szene-wand)"/>
          <circle cx="1.5" cy="-3.7" r="1.05" fill="var(--szene-stamm)"/>
          <path d="M2.4-3.85L3.5-3.5L2.4-3.15Z" fill="var(--szene-mond)"/>
        </g>
      </g>`;
}

function stillesDetail(zeit: Tageszeit, lage: Wetterlage): string {
  if (lage === "regen") {
    return `<g class="szene-wandlampe" aria-hidden="true">
      <circle cx="8.9" cy="-8" r="7.5" fill="url(#szene-fensterschein)"/>
      <rect x="7.7" y="-9.6" width="2.4" height="3.4" rx="0.6" fill="var(--szene-fensterlicht)"/>
      <path d="M7.4-9.6h3" stroke="var(--szene-dach)" stroke-width="0.7" stroke-linecap="round"/>
    </g>`;
  }
  if (zeit === "abend" || zeit === "nacht") {
    return `<g class="szene-hausstern" aria-hidden="true">
      <circle cx="-17" cy="-44" r="7" fill="url(#szene-sternschein)"/>
      <path d="M-17-47.4C-16.6-45.2-16-44.6-13.6-44C-16-43.4-16.6-42.8-17-40.6C-17.4-42.8-18-43.4-20.4-44C-18-44.6-17.4-45.2-17-47.4Z" fill="var(--szene-mond)"/>
    </g>`;
  }
  return `<g class="szene-paerchen" aria-hidden="true">
      ${spatz(-14.5, -19.1, false, "")}
      ${spatz(-5.8, -26.2, true, " szene-spatz-huepf--zwei")}
    </g>`;
}

/**
 * Das Haus — ein Piktogramm. (07.10.2026, Entwurf „ruhiger")
 *
 * Martin: *„das Haus ein wenig weniger aufgeregt, piktogrammmäßiger, nicht
 * zu detailreich, aber so schön, wie es jetzt gerade ist, auf jeden Fall
 * lassen."* Am 02.10. hatte es Ziegelreihen, Läden, Blumenkasten, Laterne,
 * Vordach, Stufe und einen Lattenzaun bekommen — gut 30 Teile auf 40
 * Punkten Breite. Auf dem Telefon las sich das als Unruhe.
 *
 * Jetzt nur, woran man ein bewohntes Haus erkennt: Wand, ein Dach mit
 * weichen Ecken, Schornstein mit Rauch, Tür, EIN Fenster mit Kreuz und
 * warmem Schein, zwei Büsche, ein kleiner Weg, ein Schatten am Boden.
 *
 * ⚠️ Keine Farbe im Markup — nur Token (--szene-wand, -dach, -tuer,
 *    -fenster, -fensterlicht, -busch, -weg, -rauch, -baumschatten), je
 *    Tageszeit und Modus im Blatt.
 * ⚠️ Wie die Bäume: `scale(1/f 1)` hält es beim Ziehen unverzerrt. Rauch und
 *    Licht bewegen sich über CSS, nie über das Attribut dieser Gruppe.
 * ⚠️ Licht: siehe DAS HAUS SCHLÄFT. Die Licht-Gruppen gibt es, sobald Licht
 *    brennen SOLL — auch um 03:00 (dann mit opacity 0), damit es um 04:50
 *    ohne Neuaufbau wieder hell werden kann. Das Fenster ist dafür zweimal
 *    da: die dunkle Scheibe (`szene-scheibe`) immer, die helle
 *    (`szene-fenster`) in der Licht-Gruppe darüber.
 */
function huette(
  zeit: Tageszeit,
  lage: Wetterlage,
  jetzt: Date,
  streckung = 1,
  jz: Jahreszeit = "sommer",
  daemmert = false,
): string {
  const f = streckung > 0 ? streckung : 1;
  const sx = Math.round((1 / f) * 1000) / 1000;
  const soll = lichtWetter(zeit, lage, jz, daemmert);
  const wach = wachAnteil(jetzt);
  const schlaf = wach === 0;
  const klasse = soll ? (schlaf ? " szene-huette--schlaf" : " szene-huette--licht") : "";
  const licht = (innen: string): string => (soll ? `<g class="szene-licht" opacity="${wach}">${innen}</g>` : "");
  // Der Mond spiegelt sich im dunklen Fenster — nur nachts.
  const spiegel =
    zeit === "nacht"
      ? '<path d="M-11.9-9.4L-9.9-6.6M-9.1-9.9L-7.4-7.6" stroke="var(--szene-mond)" stroke-opacity="0.45" stroke-width="0.6" stroke-linecap="round"/>'
      : "";
  // Kein Rauch, solange geschlafen wird — das Feuer ist aus.
  const rauch = schlaf
    ? ""
    : `<g class="szene-rauchfahne" opacity="${wach}">
        <circle class="szene-rauch" cx="11.5" cy="-30" r="2" fill="var(--szene-rauch)"/>
        <circle class="szene-rauch szene-rauch--zwei" cx="11.5" cy="-30" r="2" fill="var(--szene-rauch)"/>
      </g>`;
  return `<defs>
      <radialGradient id="szene-fensterschein">
        <stop offset="0%" stop-color="var(--szene-fensterlicht)" stop-opacity="0.8"/>
        <stop offset="100%" stop-color="var(--szene-fensterlicht)" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="szene-sternschein">
        <stop offset="0%" stop-color="var(--szene-mond)" stop-opacity="0.45"/>
        <stop offset="100%" stop-color="var(--szene-mond)" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <g class="szene-huette${klasse}" transform="translate(200 116) scale(${sx} 1)">
      <path d="M-2.5 0L2.5 0L13 24L-13 24Z" fill="var(--szene-weg)" fill-opacity="0.9"/>
      <path d="${kreisPfad([[-24, 0, 4.2], [25, 1, 3.8]])}" fill="var(--szene-busch)"/>
      <ellipse cx="0" cy="0.6" rx="20" ry="1.8" fill="var(--szene-baumschatten)" fill-opacity="0.25"/>
      <rect x="9" y="-28" width="5" height="11" rx="1" fill="var(--szene-dach)"/>
      <rect x="-17" y="-15" width="34" height="15" rx="1.6" fill="var(--szene-wand)"/>
      <path d="M-20.5-12.8L0-29.5L20.5-12.8Z" fill="var(--szene-dach)" stroke="var(--szene-dach)" stroke-width="2.2" stroke-linejoin="round"/>
      ${jz === "winter" ? `<path d="${SCHNEE_DACH}" fill="var(--szene-schnee)"/>` : ""}
      <path d="M-3 0V-4.8a3 3 0 0 1 6 0V0Z" fill="var(--szene-tuer)"/>
      ${licht('<circle class="szene-fensterschein" cx="-9.5" cy="-7.6" r="9" fill="url(#szene-fensterschein)"/>')}
      <rect class="szene-scheibe" x="-12.6" y="-10.4" width="6.2" height="5.2" rx="1" fill="var(--szene-fenster)"/>
      ${spiegel}
      ${licht('<rect class="szene-fenster" x="-12.6" y="-10.4" width="6.2" height="5.2" rx="1" fill="var(--szene-fensterlicht)"/>')}
      <path d="M-9.5-10.4V-5.2M-12.6-7.8H-6.4" stroke="var(--szene-wand)" stroke-width="0.7"/>
      ${rauch}
    </g>
    ${/*
      08.10.2026: das stille Detail steht NEBEN dem Haus, nicht darin — die
      Haus-Gruppe zählt ihre Teile (szene-haus-schlaf: „höchstens 18"), und
      ein Spatz ist kein Teil des Hauses. Gleiche Stelle, gleicher Maßstab.
    */ ""}
    <g class="szene-haus-detail" transform="translate(200 116) scale(${sx} 1)">
      ${stillesDetail(zeit, lage)}
    </g>`;
}

/**
 * Die kleine Schwester der Szene — fuer die Wetter-Kachel.
 *
 * Eigene Koordinaten statt eines Ausschnitts: Gestirn und Wolke ruecken
 * zusammen, die Huegel werden zu einer Linie. Bei 92x96 traegt nur, was
 * gross genug ist, um erkannt zu werden.
 */
function kompakteSzene(lage: Wetterlage, zeit: Tageszeit, jz: Jahreszeit): string {
  /*
   * ⚠️ 2026-08-29, zweite Runde. Die Kachel hatte drei eigene Fehler — und
   * einer davon war einer, den die BREITE Szene laengst behoben hatte:
   *
   *   1. Der Baum war ein Kreis mit Stamm. Im Kommentar der breiten Szene
   *      steht woertlich: „Kreis plus Stamm sah aus wie ein LUTSCHER."
   *      Die Lehre stand da, die Kachel hatte sie nie bekommen.
   *   2. Sonne und Mond lagen AUF der Wolke — zwei Umrisse uebereinander,
   *      bei 92 Punkten Breite nicht mehr auseinanderzuhalten.
   *   3. Der Stand aenderte sich nicht mit der Tageszeit, obwohl die grosse
   *      Szene das seit heute tut.
   *
   * Die Wolke sitzt jetzt links unten, das Gestirn rechts oben. Bei dieser
   * Groesse traegt nur, was sich nicht beruehrt.
   */
  const STAND: Record<Tageszeit, { x: number; y: number; r: number }> = {
    // ⚠️ x=66 liess den Sonnenkreis die Wolkenkante schneiden — zwei
    // Striche, die sich kreuzen, lesen sich bei 92 Punkten als Fehler.
    morgen: { x: 70, y: 36, r: 11 },
    mittag: { x: 68, y: 24, r: 12 },
    abend: { x: 74, y: 36, r: 11 },
    nacht: { x: 70, y: 26, r: 12 },
  };
  const p = STAND[zeit];
  const gestirnKlein =
    zeit === "nacht"
      ? /*
         * 07.10.2026: Sterne in der Sternfarbe (immer die helle Seite) — der
         * Nachthimmel der Kachel ist seit dem ruhigen Look dunkles Blaugrün,
         * Tinte darauf wären dunkle Punkte (dieselbe Lehre wie am 06.09.).
         */
        `<g fill="var(--szene-stern-farbe)" opacity="0.6">
           <circle cx="20" cy="16" r="1.2"/><circle cx="40" cy="10" r="1.2"/>
           <circle cx="52" cy="46" r="1.2"/><circle cx="84" cy="52" r="1.2"/>
         </g>
         <path d="M${p.x + p.r} ${p.y}a${p.r} ${p.r} 0 1 1-${p.r * 1.12} -${p.r * 0.995}A${p.r * 0.79} ${p.r * 0.79} 0 0 0 ${p.x + p.r} ${p.y}Z"
           fill="var(--szene-mond)" fill-opacity="0.20"
           stroke="var(--szene-mond)" stroke-width="1.6" stroke-opacity="0.8"/>`
      : `<circle cx="${p.x}" cy="${p.y}" r="${p.r}"
           fill="var(--szene-sonne)" fill-opacity="0.20"
           stroke="var(--szene-sonne)" stroke-width="1.6" stroke-opacity="0.8"/>
         ${strahlenKlein(p.x, p.y, p.r)}`;
  /*
   * 07.10.2026 (Prüfbefund): Wolke und Regen tragen `--szene-strich` statt
   * `--text-soft`. Seit der Nachthimmel auch im hellen Modus dunkles Blaugrün
   * ist, war die Tinte darauf kaum zu sehen; der Strich ist nachts hell.
   */
  const wolkeKlein =
    lage === "sonne"
      ? ""
      : /*
         * ⚠️ Kleiner und tiefer als im ersten Anlauf. Dort mass der Test den
         * kuerzesten Weg vom Sonnenmittelpunkt zur Wolke: 4 Einheiten bei
         * Radius 11 — die Sonne schnitt sie, morgens deutlich. Auf 92
         * Punkten bleibt neben einer Wolke dieser Groesse kein Platz fuer
         * ein Gestirn. Nicht die Regel wurde gelockert, die Wolke wurde
         * kleiner.
         */
        `<path d="M14 64h30a10 10 0 0 0 1-19.9A13 13 0 0 0 20 40 9 9 0 0 0 14 64Z"
           stroke="var(--szene-strich)" stroke-width="1.6" opacity="0.5"/>`;
  const regenKlein =
    lage === "regen"
      ? `<g stroke="var(--szene-strich)" stroke-width="1.5" stroke-linecap="round" opacity="0.38">
           <path d="M22 69l-3 8M33 69l-3 8M44 69l-3 8"/>
         </g>`
      : "";
  return `<div class="zuhause-szene" data-zeit="${zeit}" data-jahreszeit="${jz}">
    <svg viewBox="0 0 92 96" width="100%" height="96" fill="none" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
      ${gestirnKlein}
      ${wolkeKlein}
      ${regenKlein}
      <path d="M0 84c14-8 24-3 36 1 14 4 24-5 38-3 8 1 14 4 18 3v11H0z" fill="var(--hairline)" opacity="0.7"/>
      ${/*
        01.10.2026 (Ticket 2892): auch die Kachel zeigt jetzt einen Laubbaum —
        Stamm und drei Kronenkreise in denselben Tokens wie die breite Szene.
        Daneben ein Nadelbaum waere ein zweites Bild vom selben Ort. Der
        Lutscher-Fehler (Kreis auf Strich) bleibt vermieden: der Stamm ist
        sichtbar kraeftig und die Krone aus drei ueberlappenden Kreisen.
      */ ""}
      <g class="szene-baum-klein">
        <rect x="19.6" y="79" width="2.8" height="10" rx="1" fill="var(--szene-stamm)"/>
        <circle cx="21" cy="75" r="6.5" fill="var(--szene-laub-1)"/>
        <circle cx="16.5" cy="79" r="4.6" fill="var(--szene-laub-1)"/>
        <circle cx="26" cy="78.5" r="5" fill="var(--szene-laub-1)"/>
        <circle cx="19.5" cy="71.5" r="3.6" fill="var(--szene-laub-2)"/>
      </g>
    </svg>
  </div>`;
}

/** Vier kurze Strahlen — mehr traegt die Kachel nicht. */
function strahlenKlein(cx: number, cy: number, r: number): string {
  const i = r + 4;
  const a = r + 9;
  const pfade: string[] = [];
  for (let k = 0; k < 8; k++) {
    const w = (k * Math.PI) / 4;
    const dx = Math.cos(w);
    const dy = Math.sin(w);
    pfade.push(
      `M${(cx + dx * i).toFixed(1)} ${(cy + dy * i).toFixed(1)}` +
        `L${(cx + dx * a).toFixed(1)} ${(cy + dy * a).toFixed(1)}`,
    );
  }
  return `<g stroke="var(--szene-sonne)" stroke-width="1.5" stroke-linecap="round" opacity="0.5">
    <path d="${pfade.join("")}"/>
  </g>`;
}
